// src/components/ApartmentDashboard.tsx
import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Avatar,
  Chip,
  IconButton,
  Button,
  Tooltip,
  Skeleton,
  Stack,
  Divider,
  LinearProgress,
  alpha,
  useTheme,
  useMediaQuery,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Badge,
  Paper,
  Checkbox,
  ListItemText,
  OutlinedInput,
  AvatarGroup,
  Popover,
  Alert,
} from "@mui/material";
import {
  Dashboard as DashboardIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  AutoAwesomeMotionRounded as ContentIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  People as PeopleIcon,
  Email as EmailIcon,
  Search as SearchIcon,
  Assignment as AssignmentIcon,
  ArrowForward as ArrowForwardIcon,
  CheckCircle as CheckCircleIcon,
  Phone as PhoneIcon,
  Inbox as InboxIcon,
  Tour as TourIcon,
  Receipt as ReceiptIcon,
  Payment as PaymentIcon,
  History as HistoryIcon,
  Close as CloseIcon,
  Warning as WarningIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  Visibility as VisibilityIcon,
  EmojiEvents as TrophyIcon,
  NotificationsActive as AlertIcon,
  Timeline as TimelineIcon,
  FilterList as FilterIcon,
  BarChart as BarChartIcon,
  BookmarkBorder as BookmarkIcon,
  PlayArrow as PlayIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
} from "recharts";
import { gradients, shadows } from "../theme/theme";
import { supabase } from "../lib/supabase";
import { fetchWithAuth } from "../lib/api";
import { buildCapabilities } from "../lib/capabilities";
import { usePersistedState } from "../hooks/usePersistedState";
import { subscribeAppResume } from "../lib/appResumeCoordinator";
import { createSingleFlightRefresh } from "../lib/singleFlightRefresh";
import {
  dashboardDaysForRange,
  dashboardRequestIsCurrent,
  type DashboardTimeRange,
} from "../lib/dashboardRefreshPolicy";
import type { ClientNavigationStatus } from "../lib/appNavigationState";
import {
  buildLeadDailyChartForRange,
  buildLeadRangeChart,
  formatLeadMoveInDate,
  type DashboardLeadRow,
  type WeeklyLeadBucket,
} from "../lib/dashboardLeads";
import {
  buildLeadDisplayName,
  buildLeadLocationParts,
  formatLeadPets,
} from "../lib/leadPreferences";
import { optionalLeadAttribution } from "../lib/leadAttribution";
import ActivityCenter from "./ActivityCenter";
// PKT-0056 · Operator Today dashboard widget
import ActivityTodaySummary from "./dashboard/ActivityTodaySummary";
import OperatorConsoleRail from "./dashboard/OperatorConsoleRail";
import WebsiteLeadLaneChart from "./dashboard/WebsiteLeadLaneChart";
import NewLeadsFeed, { isUnclaimedRow, selectNewLeads } from "./dashboard/NewLeadsFeed";
// PKT-0306: YourClients replaced TasksDueToday (component deleted 2026-08-01 —
// it was mounted nowhere; the cadence checklist + task CRUD live in the client
// DetailDrawer, not in that widget).
import YourClients from "./dashboard/YourClients";
import LeadDetailModal from "./dashboard/LeadDetailModal";
import PageHeaderTitle from "./PageHeaderTitle";
import PageRefreshButton from "./PageRefreshButton";
import { LoadingTopBar } from "./common/PageLoadingScaffold";
import { DEMO_SAVED_SEARCHES } from "../constants/demoSavedSearches";
import { runLeadAction, type LeadActionKind } from "../services/leadConversionService";
import {
  fetchLeadOpportunities,
  type UnclaimedLeadTruth,
} from "../services/leadOpportunityService";
import TenPctPathLine from "./dashboard/TenPctPathLine";
// PKT-0916 (C) · Owed / Collected / Awaiting invoice, derived from invoices.
import MoneyPanel from "./dashboard/MoneyPanel";

// ==================== Types ====================

interface Activity {
  id: string;
  user_id: string;
  activity_type: string;
  entity_type: string;
  entity_id?: string;
  metadata: Record<string, any>;
  created_at: string;
  user?: Agent;
}

interface Agent {
  id: string;
  full_name: string;
  email: string;
  avatar_url?: string;
}

interface Client {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  status: string;
  priority: string;
  last_contacted_at?: string;
  assigned_agent_id?: string;
  assigned_agent?: Agent;
}

interface Task {
  id: string;
  client_id: string;
  title: string;
  task_type: string;
  status: string;
  due_date?: string;
  created_at: string;
  clients?: {
    id: string;
    first_name: string;
    last_name: string;
  };
}

interface DashboardStats {
  total_clients: number;
  active_clients: number;
  new_leads: number;
  urgent_clients: number;
  overdue_tasks: number;
}

interface DashboardActivityOverview {
  agents: Agent[];
  activity_counts: Record<string, number>;
  chart_data: any[];
}

type DashboardChartMode = "leads" | "activity";

interface DashboardSearchCriteria {
  min_price?: number | null;
  max_price?: number | null;
  selected_bedrooms?: string[];
  min_sqft?: number | null;
  available_only?: boolean;
  amenities?: string[];
  neighborhoods?: string[];
  price_per_sqft_max?: number | null;
  sort_by?: string;
  move_in_date?: string | null;
  selected_unit_ids?: string[];
  selected_floor_plan_keys?: string[];
  selection_mode?: "floorplan" | "unit";
}

interface DashboardSavedSearch {
  id: string;
  client_id: string;
  name: string;
  search_criteria: DashboardSearchCriteria;
  result_count?: number;
  created_at?: string;
  client_name?: string;
}

// ==================== Constants ====================

const AGENT_COLORS = [
  "#6366F1", // Indigo
  "#10B981", // Emerald
  "#F97316", // Orange
  "#EC4899", // Pink
  "#8B5CF6", // Violet
  "#14B8A6", // Teal
  "#F59E0B", // Amber
  "#EF4444", // Red
  "#3B82F6", // Blue
  "#84CC16", // Lime
];

const getWebsiteLeadsForBucketRange = (
  leads: DashboardLeadRow[],
  bucket: WeeklyLeadBucket | null,
): DashboardLeadRow[] => {
  if (!bucket?.isoDate) return [];
  const endIsoDate = bucket.endIsoDate || bucket.isoDate;
  return leads.filter((lead) => {
    if (!lead.created_at) return false;
    const createdAt = new Date(lead.created_at);
    if (Number.isNaN(createdAt.getTime())) return false;
    const createdDateKey = [
      createdAt.getFullYear(),
      String(createdAt.getMonth() + 1).padStart(2, "0"),
      String(createdAt.getDate()).padStart(2, "0"),
    ].join("-");
    return createdDateKey >= bucket.isoDate && createdDateKey <= endIsoDate;
  });
};

const formatBucketIsoDate = (isoDate: string | undefined): string => {
  if (!isoDate) return "";
  const match = isoDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return isoDate;
  return new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3])
  ).toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const formatWebsiteLeadBucketTitle = (bucket: WeeklyLeadBucket): string => {
  if (!bucket.endIsoDate || bucket.endIsoDate === bucket.isoDate) {
    const dateText = formatBucketIsoDate(bucket.isoDate);
    if (!dateText) return bucket.date;
    return /^(Mon|Tue|Wed|Thu|Fri|Sat|Sun)$/i.test(bucket.date)
      ? `${bucket.date}, ${dateText}`
      : dateText;
  }
  return `${formatBucketIsoDate(bucket.isoDate)} - ${formatBucketIsoDate(
    bucket.endIsoDate
  )}`;
};

const isMultiDayLeadBucket = (bucket: WeeklyLeadBucket | null): boolean =>
  Boolean(bucket?.endIsoDate && bucket.endIsoDate !== bucket.isoDate);

const buildWeeklyActivityChartData = (chartData: any[]): any[] => {
  const bucketSize = 7;
  const weeklyBuckets: any[] = [];

  for (let index = 0; index < chartData.length; index += bucketSize) {
    const chunk = chartData.slice(index, index + bucketSize);
    if (chunk.length === 0) continue;

    const first = chunk[0];
    const last = chunk[chunk.length - 1];
    const dateRange =
      first.date === last.date ? first.date : `${first.date} - ${last.date}`;
    const bucket: any = {
      date: first.date,
      dateRange,
      breakdown: {},
    };

    chunk.forEach((day) => {
      Object.entries(day || {}).forEach(([key, value]) => {
        if (!key.endsWith("_total")) return;
        bucket[key] = (bucket[key] || 0) + Number(value || 0);
      });

      Object.entries(day?.breakdown || {}).forEach(
        ([agentId, breakdown]: [string, any]) => {
          if (!bucket.breakdown[agentId]) {
            bucket.breakdown[agentId] = {};
          }
          Object.entries(breakdown || {}).forEach(([type, count]) => {
            bucket.breakdown[agentId][type] =
              (bucket.breakdown[agentId][type] || 0) + Number(count || 0);
          });
        }
      );
    });

    weeklyBuckets.push(bucket);
  }

  return weeklyBuckets;
};

const renderActivityChartDot = (props: any, radius: number) => {
  const value = Number(props?.value ?? props?.payload?.[props?.dataKey] ?? 0);
  if (!Number.isFinite(value) || value <= 0) {
    return <g />;
  }

  return (
    <circle
      cx={props.cx}
      cy={props.cy}
      r={radius}
      fill="#1e293b"
      stroke={props.stroke}
      strokeWidth={2}
    />
  );
};

const buildDashboardLeadPreferenceSummary = (
  lead: DashboardLeadRow
): { text: string; captured: boolean } => {
  const locationParts = buildLeadLocationParts(
    lead.neighborhoods,
    lead.neighborhood_other
  );
  const pets = formatLeadPets(lead.pets);
  const text = [
    locationParts.slice(0, 2).join(", "),
    pets ? `Pets: ${pets}` : "",
  ]
    .filter(Boolean)
    .join(" · ");

  return text
    ? { text, captured: true }
    : { text: "Preferences not captured", captured: false };
};

const formatDashboardLeadBudget = (lead: DashboardLeadRow): string => {
  const textBudget = (lead.budget || "").trim();
  if (textBudget) return textBudget;
  if (typeof lead.budget_number === "number" && Number.isFinite(lead.budget_number)) {
    return formatCurrencyShort(lead.budget_number);
  }
  return "Budget missing";
};

const formatDashboardLeadNeighborhood = (
  lead: DashboardLeadRow
): string => {
  const parts = buildLeadLocationParts(lead.neighborhoods, lead.neighborhood_other);
  if (parts.length === 0) {
    return "";
  }
  const visible = parts.slice(0, 2).join(", ");
  return parts.length > 2 ? `${visible} +${parts.length - 2}` : visible;
};

const WebsiteLeadBucketSidebar: React.FC<{
  bucket: WeeklyLeadBucket;
  leads: DashboardLeadRow[];
  onClose: () => void;
  onOpenLeads: () => void;
  onRowClick: (lead: DashboardLeadRow) => Promise<void> | void;
  agentsById: Map<string, Agent>;
  currentUserId: string | null;
  onConvert: (lead: DashboardLeadRow) => Promise<void>;
}> = ({
  bucket,
  leads,
  onClose,
  onOpenLeads,
  onRowClick,
  agentsById,
  currentUserId,
  onConvert,
}) => {
  const hasMoreLeads = leads.length > 5;
  const visibleLeadLimit = hasMoreLeads ? 4 : 5;
  const visibleLeads = leads.slice(0, visibleLeadLimit);
  const hiddenLeadCount = Math.max(0, leads.length - visibleLeads.length);
  const bucketTitle = formatWebsiteLeadBucketTitle(bucket);

  return (
    <Box
      sx={{
        width: { xs: "100%", md: 360 },
        // Mobile: size to content — the visible-lead cap (~5 rows) bounds it,
        // so a 2-lead day doesn't leave a tall empty gap (Grant 2026-07-11).
        height: { xs: "auto", md: "100%" },
        minHeight: { md: 300 },
        flexShrink: 0,
        pl: { xs: 0, md: 2 },
        pt: { xs: 1.5, md: 0 },
        ml: { xs: 0, md: 1 },
        borderLeft: { xs: 0, md: "1px solid rgba(99, 102, 241, 0.24)" },
        borderTop: { xs: "1px solid rgba(99, 102, 241, 0.18)", md: 0 },
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.25}>
        <Box minWidth={0}>
          <Typography
            variant="subtitle1"
            fontWeight={800}
            sx={{ color: "#cbd5e1", letterSpacing: 0 }}
            noWrap
          >
            {bucketTitle}
          </Typography>
          <Typography variant="caption" sx={{ color: "#94a3b8" }}>
            {leads.length} {leads.length === 1 ? "lead" : "leads"}
          </Typography>
        </Box>
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
          <Button
            size="small"
            endIcon={<ArrowForwardIcon />}
            onClick={onOpenLeads}
            sx={{
              minWidth: 0,
              px: 0.5,
              color: "#60a5fa",
              textTransform: "none",
              fontSize: "0.75rem",
              fontWeight: 800,
            }}
          >
            Open
          </Button>
          <Tooltip title="Close">
            <IconButton size="small" onClick={onClose} sx={{ color: "#94a3b8", mt: -0.25 }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
          borderTop: "1px solid rgba(148, 163, 184, 0.14)",
        }}
      >
        {leads.length > 0 ? (
          visibleLeads.map((lead) => {
            const title =
              buildLeadDisplayName(lead) ||
              lead.email ||
              lead.phone ||
              "Unnamed lead";
            const budget = formatDashboardLeadBudget(lead);
            const neighborhood = formatDashboardLeadNeighborhood(lead);
            const moveInDate = formatLeadMoveInDate(lead.move_in_date);
            // PKT-0050 · read JOIN-resolved field (was lead.assigned_agent_id
            // before migration 033 dropped the denormalized cache column).
            const assignedAgent = lead.resolved_assigned_agent_id
              ? agentsById.get(lead.resolved_assigned_agent_id)
              : null;
            const isClaimedByMe =
              !!currentUserId &&
              lead.resolved_assigned_agent_id === currentUserId;
            return (
              <Box
                key={lead.id}
                onClick={() => void onRowClick(lead)}
                sx={{
                  minHeight: 46,
                  px: 0.2,
                  py: 0.95,
                  cursor: "pointer",
                  borderBottom: "1px solid rgba(148, 163, 184, 0.1)",
                  transition: "background-color 160ms ease",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  "&:hover": {
                    bgcolor: "rgba(96, 165, 250, 0.06)",
                  },
                }}
              >
                {/* Left rail: avatar (if claimed) OR Claim button (if not) */}
                <Box
                  sx={{
                    flexShrink: 0,
                    width: 28,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  {assignedAgent ? (
                    <Tooltip
                      title={
                        isClaimedByMe
                          ? `Assigned to you (${assignedAgent.full_name || "Agent"})`
                          : `Assigned to ${assignedAgent.full_name || "Agent"}`
                      }
                      placement="left"
                    >
                      <Avatar
                        src={assignedAgent.avatar_url || undefined}
                        alt={assignedAgent.full_name || "Agent"}
                        sx={{
                          width: 24,
                          height: 24,
                          fontSize: "0.7rem",
                          fontWeight: 700,
                          bgcolor: "rgba(96, 165, 250, 0.2)",
                          color: "#bfdbfe",
                          border: isClaimedByMe
                            ? "1.5px solid rgba(34, 197, 94, 0.6)"
                            : "1.5px solid rgba(96, 165, 250, 0.35)",
                        }}
                      >
                        {(assignedAgent.full_name || "A")
                          .charAt(0)
                          .toUpperCase()}
                      </Avatar>
                    </Tooltip>
                  ) : currentUserId ? (
                    <Tooltip title="Convert to client" placement="left">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          void onConvert(lead);
                        }}
                        sx={{
                          width: 24,
                          height: 24,
                          color: "#60a5fa",
                          border: "1px dashed rgba(96, 165, 250, 0.45)",
                          bgcolor: "transparent",
                          transition:
                            "background-color 120ms ease, border-color 120ms ease",
                          "&:hover": {
                            bgcolor: "rgba(96, 165, 250, 0.14)",
                            borderColor: "rgba(96, 165, 250, 0.75)",
                            borderStyle: "solid",
                          },
                        }}
                      >
                        <AddIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <Box sx={{ width: 24, height: 24 }} />
                  )}
                </Box>

                {/* Middle: name/budget + neighborhood · time-since */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "minmax(0, 1fr) auto",
                      columnGap: 1,
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      sx={{ color: "#f8fafc", lineHeight: 1.2 }}
                      noWrap
                    >
                      {title}
                    </Typography>
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      sx={{
                        color:
                          budget === "Budget missing" ? "#64748b" : "#60a5fa",
                        lineHeight: 1.2,
                      }}
                      noWrap
                    >
                      {budget}
                    </Typography>
                  </Box>
                  {(neighborhood || moveInDate) && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#cbd5e1",
                        display: "block",
                        lineHeight: 1.2,
                        mt: 0.2,
                        textDecoration: "none",
                      }}
                      noWrap
                    >
                      {neighborhood}
                      {neighborhood && moveInDate && (
                        <Box
                          component="span"
                          sx={{ color: "#64748b", mx: 0.5 }}
                        >
                          ·
                        </Box>
                      )}
                      {moveInDate && (
                        <Box component="span" sx={{ color: "#94a3b8" }}>
                          Move-in {moveInDate}
                        </Box>
                      )}
                    </Typography>
                  )}
                </Box>

              </Box>
            );
          })
        ) : (
          <Box py={3} textAlign="center">
            <Typography variant="body2" color="text.secondary">
              No website leads in this bucket
            </Typography>
          </Box>
        )}
      </Box>
      {hiddenLeadCount > 0 && (
        <Box
          onClick={onOpenLeads}
          sx={{
            mt: 0.7,
            px: 1.1,
            py: 0.65,
            borderRadius: 1,
            cursor: "pointer",
            bgcolor: "rgba(15, 23, 42, 0.28)",
            border: "1px solid rgba(96, 165, 250, 0.24)",
            color: "#93c5fd",
            textAlign: "center",
            fontSize: "0.78rem",
            fontWeight: 800,
            "&:hover": {
              bgcolor: "rgba(96, 165, 250, 0.1)",
              borderColor: "rgba(96, 165, 250, 0.46)",
            },
          }}
        >
          +{hiddenLeadCount} more in Incoming Leads
        </Box>
      )}
    </Box>
  );
};

const ACTIVITY_CONFIG: Record<
  string,
  { icon: string; color: string; label: string }
> = {
  client_created: { icon: "👤", color: "#4CAF50", label: "Created client" },
  client_updated: { icon: "✏️", color: "#2196F3", label: "Updated client" },
  call_logged: { icon: "📞", color: "#009688", label: "Logged call" },
  spoke_on_phone: { icon: "📞", color: "#009688", label: "Spoke on phone" },
  email_sent: { icon: "📧", color: "#00BCD4", label: "Sent email" },
  emailed: { icon: "📧", color: "#00BCD4", label: "Sent email" },
  sent_text: { icon: "💬", color: "#9C27B0", label: "Sent text" },
  left_voicemail: { icon: "📱", color: "#FF9800", label: "Left voicemail" },
  meeting_scheduled: {
    icon: "📅",
    color: "#E91E63",
    label: "Scheduled meeting",
  },
  note_added: { icon: "📝", color: "#795548", label: "Added note" },
  task_created: { icon: "✅", color: "#FF5722", label: "Created task" },
  task_completed: { icon: "🎉", color: "#4CAF50", label: "Completed task" },
  tour_scheduled: { icon: "🚶", color: "#00BCD4", label: "Scheduled tour" },
  invoice_sent: { icon: "💵", color: "#FFC107", label: "Sent invoice" },
  payment_received: { icon: "💰", color: "#4CAF50", label: "Payment received" },
  listing_shared: { icon: "📤", color: "#3F51B5", label: "Shared listings" },
  search_executed: { icon: "🔍", color: "#9C27B0", label: "Executed search" },
  search_saved: { icon: "💾", color: "#673AB7", label: "Saved search" },
  guest_card_sent: { icon: "📋", color: "#FF9800", label: "Sent guest card" },
  status_changed: { icon: "🔄", color: "#2196F3", label: "Changed status" },
  lease_uploaded: { icon: "📄", color: "#607D8B", label: "Uploaded lease" },
  gift_card_sent: { icon: "🎁", color: "#E91E63", label: "Sent gift card" },
};

const PIPELINE_STAGES = [
  { key: "new_client", label: "New Client", shortLabel: "New", color: "#F59E0B" },
  { key: "touring", label: "Touring", shortLabel: "Tour", color: "#0EA5E9" },
  { key: "applied", label: "Applied", shortLabel: "Applied", color: "#8B5CF6" },
  { key: "invoice_sent", label: "Invoice Sent", shortLabel: "Invoiced", color: "#06B6D4" },
  { key: "payment_received", label: "Payment Received", shortLabel: "Paid", color: "#10B981" },
];

// ==================== Helpers ====================

const getActivityConfig = (type: string) => {
  return (
    ACTIVITY_CONFIG[type] || {
      icon: "📌",
      color: "#757575",
      label: type.replace(/_/g, " "),
    }
  );
};

const formatActivityDescription = (activity: Activity): string => {
  const { activity_type, metadata } = activity;
  const clientName = metadata?.client_name || "Unknown Client";
  const config = getActivityConfig(activity_type);

  switch (activity_type) {
    case "client_created":
      return `Created new client: ${clientName}`;
    case "client_updated":
      return `Updated client: ${clientName}`;
    case "call_logged":
    case "spoke_on_phone":
      return `Call with ${clientName}`;
    case "email_sent":
    case "emailed":
      return `Email to ${clientName}`;
    case "sent_text":
      return `Text to ${clientName}`;
    case "left_voicemail":
      return `Voicemail for ${clientName}`;
    case "note_added":
      return `Note for ${clientName}`;
    case "task_created":
      return `Task created for ${clientName}`;
    case "task_completed":
      return `Task completed for ${clientName}`;
    case "tour_scheduled":
      return `Tour scheduled for ${clientName}`;
    case "invoice_sent":
      return `Invoice sent to ${clientName}`;
    case "payment_received":
      return `Payment from ${clientName}`;
    case "listing_shared":
      return `Shared ${metadata?.unit_count || ""} listings with ${clientName}`;
    case "search_executed":
      return `Search executed (${metadata?.result_count || 0} results)`;
    case "search_saved":
      return `Saved search for ${clientName}`;
    case "guest_card_sent":
      return `Guest card sent for ${clientName}`;
    case "status_changed":
      return `Status changed for ${clientName}`;
    case "lease_uploaded":
      return `Lease uploaded for ${clientName}`;
    case "gift_card_sent":
      return `Gift card sent to ${clientName}`;
    default:
      return `${config.label} - ${clientName}`;
  }
};

const formatRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

const formatFullDate = (dateString: string): string => {
  return new Date(dateString).toLocaleString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

const formatCurrencyShort = (value: number): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);
};

const buildSavedSearchSummary = (criteria: DashboardSearchCriteria): string => {
  const parts: string[] = [];

  if (criteria.selected_bedrooms?.length) {
    parts.push(`${criteria.selected_bedrooms.join(", ")} bed`);
  }

  const hasMinPrice = typeof criteria.min_price === "number";
  const hasMaxPrice = typeof criteria.max_price === "number";
  if (hasMinPrice && hasMaxPrice) {
    parts.push(
      `${formatCurrencyShort(criteria.min_price as number)} - ${formatCurrencyShort(criteria.max_price as number)}`
    );
  } else if (hasMinPrice) {
    parts.push(`From ${formatCurrencyShort(criteria.min_price as number)}`);
  } else if (hasMaxPrice) {
    parts.push(`Up to ${formatCurrencyShort(criteria.max_price as number)}`);
  }

  if (typeof criteria.min_sqft === "number") {
    parts.push(`${criteria.min_sqft}+ sqft`);
  }

  if (criteria.neighborhoods?.length) {
    if (criteria.neighborhoods.length === 1) {
      parts.push(criteria.neighborhoods[0]);
    } else {
      parts.push(`${criteria.neighborhoods[0]} +${criteria.neighborhoods.length - 1}`);
    }
  }

  if (criteria.available_only) {
    parts.push("Available now");
  }

  return parts.slice(0, 3).join(" · ") || "Custom criteria";
};

// ==================== Sub-Components ====================

interface StatCardProps {
  title: string;
  value: string | number;
  change?: number;
  changeLabel?: string;
  icon: React.ReactNode;
  color: string;
  gradient?: string;
  loading?: boolean;
  onClick?: (e?: React.MouseEvent<HTMLElement>) => void;
  badge?: number;
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  change,
  changeLabel,
  icon,
  color,
  gradient,
  loading,
  onClick,
  badge,
}) => {
  const theme = useTheme();
  const isMobileCard = useMediaQuery(theme.breakpoints.down("sm"));

  const cardSx = {
    height: "100%",
    cursor: onClick ? "pointer" : "default",
    position: "relative",
    overflow: "hidden",
    transition: "all 0.3s ease",
    "&:hover": onClick
      ? { transform: "translateY(-2px)", boxShadow: `0 8px 16px ${alpha(color, 0.2)}` }
      : {},
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0, left: 0, right: 0,
      height: 3,
      background: gradient || color,
    },
  };

  if (isMobileCard) {
    return (
      <Card sx={cardSx} onClick={onClick}>
        <CardContent sx={{ py: 1.25, px: 2, "&:last-child": { pb: 1.25 } }}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box sx={{ color, display: "flex", "& svg": { fontSize: 22 } }}>
              {icon}
            </Box>
            {loading ? (
              <Skeleton width={40} height={28} />
            ) : (
              <Typography variant="h6" fontWeight={700}>
                {typeof value === "number" ? value.toLocaleString() : value}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              {title}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={cardSx} onClick={onClick}>
      <CardContent sx={{ p: 3 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, opacity: 0.7 }}>
              {title}
            </Typography>
            {loading ? (
              <Skeleton width={80} height={40} />
            ) : (
              <Typography variant="h3" fontWeight={700}>
                {typeof value === "number" ? value.toLocaleString() : value}
              </Typography>
            )}
            {change !== undefined && (
              <Box display="flex" alignItems="center" gap={0.5} mt={1}>
                {change >= 0 ? (
                  <TrendingUpIcon
                    sx={{ fontSize: 16, color: theme.palette.success.main }}
                  />
                ) : (
                  <TrendingDownIcon
                    sx={{ fontSize: 16, color: theme.palette.error.main }}
                  />
                )}
                <Typography
                  variant="caption"
                  sx={{
                    color:
                      change >= 0
                        ? theme.palette.success.main
                        : theme.palette.error.main,
                    fontWeight: 600,
                  }}
                >
                  {change >= 0 ? "+" : ""}
                  {change}%
                </Typography>
                {changeLabel && (
                  <Typography variant="caption" sx={{ opacity: 0.6, ml: 0.5 }}>
                    {changeLabel}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
          <Badge badgeContent={badge} color="error" invisible={!badge}>
            <Avatar
              sx={{
                width: 56,
                height: 56,
                background: alpha(color, 0.15),
                color: color,
              }}
            >
              {icon}
            </Avatar>
          </Badge>
        </Box>
      </CardContent>
    </Card>
  );
};

// Activity Detail Dialog
interface ActivityDetailDialogProps {
  activity: Activity | null;
  open: boolean;
  onClose: () => void;
  onViewClient: (clientId: string) => void;
}

const ActivityDetailDialog: React.FC<ActivityDetailDialogProps> = ({
  activity,
  open,
  onClose,
  onViewClient,
}) => {
  const theme = useTheme();

  if (!activity) return null;

  const config = getActivityConfig(activity.activity_type);
  const clientId = activity.entity_id;
  const clientName = activity.metadata?.client_name || "Unknown Client";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: "#1e293b",
          color: "#e2e8f0",
        },
      }}
    >
      <DialogTitle sx={{ borderBottom: "1px solid #334155" }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={2}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: alpha(config.color, 0.2),
                fontSize: "1.5rem",
              }}
            >
              {config.icon}
            </Box>
            <Box>
              <Typography variant="h6" sx={{ color: "#f1f5f9" }}>
                {config.label}
              </Typography>
              <Typography variant="caption" sx={{ color: "#64748b" }}>
                Activity Details
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} sx={{ color: "#94a3b8" }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        <Stack spacing={3}>
          {/* Client Info */}
          <Box sx={{ p: 2, backgroundColor: "#334155", borderRadius: 2 }}>
            <Typography
              variant="caption"
              sx={{ color: "#64748b", display: "block", mb: 0.5 }}
            >
              Client
            </Typography>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box display="flex" alignItems="center" gap={1.5}>
                <Avatar
                  sx={{ width: 32, height: 32, background: gradients.primary }}
                >
                  {clientName.charAt(0)}
                </Avatar>
                <Typography
                  variant="body1"
                  sx={{ color: "#e2e8f0", fontWeight: 500 }}
                >
                  {clientName}
                </Typography>
              </Box>
              {clientId && activity.entity_type === "client" && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<VisibilityIcon />}
                  onClick={() => {
                    onViewClient(clientId);
                    onClose();
                  }}
                  sx={{
                    color: "#60a5fa",
                    borderColor: "#3b82f6",
                    "&:hover": {
                      borderColor: "#60a5fa",
                      backgroundColor: "#3b82f620",
                    },
                  }}
                >
                  View Client
                </Button>
              )}
            </Box>
          </Box>

          {/* Timestamp */}
          <Box>
            <Typography
              variant="caption"
              sx={{ color: "#64748b", display: "block", mb: 0.5 }}
            >
              Date & Time
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <CalendarIcon sx={{ fontSize: 18, color: "#64748b" }} />
              <Typography variant="body2" sx={{ color: "#e2e8f0" }}>
                {formatFullDate(activity.created_at)}
              </Typography>
            </Box>
          </Box>

          {/* Agent */}
          {activity.user && (
            <Box>
              <Typography
                variant="caption"
                sx={{ color: "#64748b", display: "block", mb: 0.5 }}
              >
                Agent
              </Typography>
              <Box display="flex" alignItems="center" gap={1.5}>
                <Avatar
                  src={activity.user.avatar_url}
                  sx={{ width: 28, height: 28 }}
                >
                  {activity.user.full_name?.charAt(0)}
                </Avatar>
                <Typography variant="body2" sx={{ color: "#e2e8f0" }}>
                  {activity.user.full_name || activity.user.email}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Notes/Details */}
          {activity.metadata?.notes && (
            <Box>
              <Typography
                variant="caption"
                sx={{ color: "#64748b", display: "block", mb: 0.5 }}
              >
                Notes
              </Typography>
              <Box
                sx={{
                  p: 2,
                  backgroundColor: "#334155",
                  borderRadius: 2,
                  borderLeft: `3px solid ${config.color}`,
                }}
              >
                <Typography variant="body2" sx={{ color: "#cbd5e1" }}>
                  {activity.metadata.notes}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Additional Metadata */}
          {(activity.metadata?.outcome ||
            activity.metadata?.subject ||
            activity.metadata?.property_name ||
            activity.metadata?.result_count !== undefined ||
            activity.metadata?.new_status) && (
            <Box>
              <Typography
                variant="caption"
                sx={{ color: "#64748b", display: "block", mb: 1 }}
              >
                Additional Details
              </Typography>
              <Stack spacing={1}>
                {activity.metadata?.outcome && (
                  <Chip
                    size="small"
                    label={`Outcome: ${activity.metadata.outcome}`}
                    sx={{ backgroundColor: "#33415580", color: "#e2e8f0" }}
                  />
                )}
                {activity.metadata?.subject && (
                  <Chip
                    size="small"
                    label={`Subject: ${activity.metadata.subject}`}
                    sx={{ backgroundColor: "#33415580", color: "#e2e8f0" }}
                  />
                )}
                {activity.metadata?.property_name && (
                  <Chip
                    size="small"
                    label={`Property: ${activity.metadata.property_name}`}
                    sx={{ backgroundColor: "#33415580", color: "#e2e8f0" }}
                  />
                )}
                {activity.metadata?.result_count !== undefined && (
                  <Chip
                    size="small"
                    label={`Results: ${activity.metadata.result_count}`}
                    sx={{ backgroundColor: "#33415580", color: "#e2e8f0" }}
                  />
                )}
                {activity.metadata?.new_status && (
                  <Chip
                    size="small"
                    label={`New Status: ${activity.metadata.new_status}`}
                    sx={{ backgroundColor: "#33415580", color: "#e2e8f0" }}
                  />
                )}
              </Stack>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ borderTop: "1px solid #334155", p: 2 }}>
        <Button onClick={onClose} sx={{ color: "#94a3b8" }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Activity Item Component
interface ActivityItemProps {
  activity: Activity;
  onClick: () => void;
}

const ActivityItem: React.FC<ActivityItemProps> = ({ activity, onClick }) => {
  const config = getActivityConfig(activity.activity_type);

  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        gap: 2,
        py: 1.5,
        px: 1.5,
        borderRadius: 2,
        cursor: "pointer",
        transition: "all 0.2s ease",
        "&:hover": {
          backgroundColor: alpha(config.color, 0.1),
          transform: "translateX(4px)",
        },
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: alpha(config.color, 0.15),
          fontSize: "1.1rem",
          flexShrink: 0,
        }}
      >
        {config.icon}
      </Box>
      <Box flex={1} minWidth={0}>
        <Typography
          variant="body2"
          sx={{ fontWeight: 500, color: "text.primary" }}
          noWrap
        >
          {formatActivityDescription(activity)}
        </Typography>
        <Box display="flex" alignItems="center" gap={1} mt={0.25}>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {formatRelativeTime(activity.created_at)}
          </Typography>
          {activity.user && (
            <>
              <Box
                sx={{
                  width: 3,
                  height: 3,
                  borderRadius: "50%",
                  backgroundColor: "text.disabled",
                }}
              />
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                {activity.user.full_name?.split(" ")[0] || "Agent"}
              </Typography>
            </>
          )}
        </Box>
      </Box>
      <ArrowForwardIcon
        sx={{ color: "text.secondary", fontSize: 18, alignSelf: "center" }}
      />
    </Box>
  );
};

// Pipeline Stage Component
interface PipelineStageProps {
  stage: { key: string; label: string; shortLabel: string; color: string };
  count: number;
  total: number;
  compact?: boolean;
  onClick: (e: React.MouseEvent<HTMLElement>) => void;
}

const PipelineStage: React.FC<PipelineStageProps> = ({
  stage,
  count,
  total,
  compact = false,
  onClick,
}) => {
  const percentage = total > 0 ? (count / total) * 100 : 0;

  return (
    <Box
      onClick={(e) => onClick(e)}
      sx={{
        flex: 1,
        textAlign: "center",
        cursor: "pointer",
        p: compact ? 1 : 2,
        borderRadius: 2,
        transition: "all 0.2s",
        "&:hover": { backgroundColor: alpha(stage.color, 0.1) },
      }}
    >
      <Typography variant={compact ? "h5" : "h4"} fontWeight={700} sx={{ color: stage.color }}>
        {count}
      </Typography>
      <Typography
        variant="caption"
        sx={{ color: "#94a3b8", display: "block", mb: 0.5, fontSize: compact ? "0.65rem" : undefined }}
      >
        {compact ? stage.shortLabel : stage.label}
      </Typography>
      <LinearProgress
        variant="determinate"
        value={percentage}
        sx={{
          height: 6,
          borderRadius: 3,
          backgroundColor: alpha(stage.color, 0.2),
          "& .MuiLinearProgress-bar": {
            backgroundColor: stage.color,
            borderRadius: 3,
          },
        }}
      />
    </Box>
  );
};

// Task Item Component
interface TaskItemProps {
  task: Task;
  onClick: () => void;
}

const TaskItem: React.FC<TaskItemProps> = ({ task, onClick }) => {
  const theme = useTheme();
  const isOverdue = task.due_date && new Date(task.due_date) < new Date();

  return (
    <Box
      onClick={onClick}
      sx={{
        p: 2,
        borderRadius: 2,
        backgroundColor: isOverdue ? alpha("#ef4444", 0.1) : "#334155",
        border: `1px solid ${isOverdue ? "#ef444450" : "#47556950"}`,
        cursor: "pointer",
        transition: "all 0.2s",
        "&:hover": {
          borderColor: isOverdue ? "#ef4444" : "#6366f1",
          transform: "translateX(4px)",
        },
      }}
    >
      <Box display="flex" alignItems="flex-start" gap={1.5}>
        {isOverdue && (
          <WarningIcon sx={{ color: "#ef4444", fontSize: 18, mt: 0.25 }} />
        )}
        <Box flex={1} minWidth={0}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            gap={1}
            mb={0.5}
          >
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{ color: "#e2e8f0" }}
              noWrap
            >
              {task.title}
            </Typography>
            {task.task_type && (
              <Chip
                size="small"
                label={task.task_type.replace(/_/g, " ")}
                sx={{
                  height: 18,
                  fontSize: "0.65rem",
                  backgroundColor: "#47556950",
                  color: "#94a3b8",
                  textTransform: "capitalize",
                }}
              />
            )}
          </Box>
          {task.clients && (
            <Box display="flex" alignItems="center" gap={0.5} mb={0.25}>
              <PersonIcon sx={{ fontSize: 13, color: "#94a3b8" }} />
              <Typography
                variant="caption"
                sx={{ color: "#cbd5e1", fontWeight: 500 }}
                noWrap
              >
                {task.clients.first_name} {task.clients.last_name}
              </Typography>
            </Box>
          )}
          {task.due_date && (
            <Box display="flex" alignItems="center" gap={0.5}>
              <CalendarIcon
                sx={{ fontSize: 12, color: isOverdue ? "#ef4444" : "#64748b" }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: isOverdue ? "#ef4444" : "#64748b",
                  fontWeight: isOverdue ? 600 : 400,
                }}
              >
                Due {new Date(task.due_date).toLocaleDateString()}
                {isOverdue && " · overdue"}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

// Client Needing Attention Component
interface ClientAttentionItemProps {
  client: Client;
  onClick: () => void;
}

const ClientAttentionItem: React.FC<ClientAttentionItemProps> = ({
  client,
  onClick,
}) => {
  const daysSinceContact = client.last_contacted_at
    ? Math.floor(
        (Date.now() - new Date(client.last_contacted_at).getTime()) / 86400000
      )
    : 999;

  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        p: 1.5,
        borderRadius: 2,
        backgroundColor: "#334155",
        cursor: "pointer",
        transition: "all 0.2s",
        "&:hover": { backgroundColor: "#3f4f63", transform: "translateX(4px)" },
      }}
    >
      <Avatar sx={{ width: 36, height: 36, background: gradients.primary }}>
        {client.first_name?.charAt(0)}
        {client.last_name?.charAt(0)}
      </Avatar>
      <Box flex={1} minWidth={0}>
        <Typography
          variant="body2"
          fontWeight={500}
          sx={{ color: "#e2e8f0" }}
          noWrap
        >
          {client.first_name} {client.last_name}
        </Typography>
        <Typography variant="caption" sx={{ color: "#f87171" }}>
          {daysSinceContact === 999
            ? "Never contacted"
            : `${daysSinceContact} days ago`}
        </Typography>
      </Box>
      <Chip
        size="small"
        label={client.status?.replace(/_/g, " ")}
        sx={{
          height: 22,
          fontSize: "0.7rem",
          backgroundColor: "#47556950",
          color: "#94a3b8",
          textTransform: "capitalize",
        }}
      />
    </Box>
  );
};

// Agent Leaderboard Item
interface LeaderboardItemProps {
  agent: Agent;
  activityCount: number;
  rank: number;
  color: string;
}

const LeaderboardItem: React.FC<LeaderboardItemProps> = ({
  agent,
  activityCount,
  rank,
  color,
}) => {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        py: 1,
      }}
    >
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: rank <= 3 ? alpha(color, 0.2) : "#33415550",
          color: rank <= 3 ? color : "#64748b",
          fontWeight: 700,
          fontSize: "0.75rem",
        }}
      >
        {rank}
      </Box>
      <Avatar src={agent.avatar_url} sx={{ width: 32, height: 32 }}>
        {agent.full_name?.charAt(0)}
      </Avatar>
      <Box flex={1} minWidth={0}>
        <Typography
          variant="body2"
          fontWeight={500}
          sx={{ color: "#e2e8f0" }}
          noWrap
        >
          {agent.full_name || agent.email}
        </Typography>
      </Box>
      <Chip
        size="small"
        label={`${activityCount} activities`}
        sx={{
          backgroundColor: alpha(color, 0.15),
          color: color,
          fontWeight: 600,
        }}
      />
    </Box>
  );
};

// Custom Chart Tooltip
const ChartTooltip = ({
  active,
  payload,
  label,
  agentNameById,
}: any) => {
  if (active && payload && payload.length) {
    const tooltipPayload = payload
      .filter((entry: any) => Number(entry.value || 0) > 0)
      .sort((a: any, b: any) => Number(b.value || 0) - Number(a.value || 0));
    const tooltipLabel = payload?.[0]?.payload?.dateRange || label;

    return (
      <Box
        sx={{
          backgroundColor: "#1e293b",
          border: "1px solid #334155",
          borderRadius: 2,
          p: 2,
          minWidth: 200,
        }}
      >
        <Typography
          variant="caption"
          sx={{ color: "#64748b", display: "block", mb: 1 }}
        >
          {tooltipLabel}
        </Typography>
        {tooltipPayload.length > 0 ? (
          tooltipPayload.slice(0, 4).map((entry: any, index: number) => (
            <Box key={index} sx={{ mb: 1 }}>
              <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    backgroundColor: entry.color,
                  }}
                />
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{ color: "#e2e8f0" }}
                >
                  {agentNameById?.[entry.dataKey.replace("_total", "")] ||
                    entry.name.replace("_total", "")}
                  : {entry.value}
                </Typography>
              </Box>
              {/* Breakdown */}
              {entry.payload?.breakdown?.[
                entry.dataKey.replace("_total", "")
              ] && (
                <Box sx={{ pl: 2.5 }}>
                  {Object.entries(
                    entry.payload.breakdown[
                      entry.dataKey.replace("_total", "")
                    ] || {}
                  )
                    .sort(([, a]: [string, any], [, b]: [string, any]) => Number(b) - Number(a))
                    .slice(0, 3)
                    .map(([type, count]: [string, any]) => (
                      <Typography
                        key={type}
                        variant="caption"
                        sx={{ color: "#94a3b8", display: "block" }}
                      >
                        {getActivityConfig(type).label}: {count}
                      </Typography>
                    ))}
                </Box>
              )}
            </Box>
          ))
        ) : (
          <Typography variant="body2" sx={{ color: "#94a3b8" }}>
            No activity
          </Typography>
        )}
        {tooltipPayload.length > 4 && (
          <Typography variant="caption" sx={{ color: "#64748b", display: "block" }}>
            +{tooltipPayload.length - 4} more agents
          </Typography>
        )}
      </Box>
    );
  }
  return null;
};

// ==================== Main Component ====================

interface ApartmentDashboardProps {
  onNavigate?: (
    page: string,
    clientId?: string,
    params?: {
      status?: ClientNavigationStatus;
      tab?: string;
      clientsTab?: "leads" | "active" | "out_of_contact";
      neighborhoods?: string[];
      searchCriteria?: DashboardSearchCriteria | null;
    }
  ) => void;
  isDemo?: boolean;
  userRole?: "agent" | "admin" | "manager" | "demo";
  canAccessAdmin?: boolean;
  canReviewContent?: boolean;
}
const isBooleanPreference = (value: unknown): value is boolean => typeof value === "boolean";
const isTimeRange = (value: unknown): value is "7d" | "30d" | "90d" =>
  value === "7d" || value === "30d" || value === "90d";
const isChartMode = (value: unknown): value is DashboardChartMode =>
  value === "leads" || value === "activity";

const ApartmentDashboard: React.FC<ApartmentDashboardProps> = ({
  onNavigate,
  isDemo = false,
  userRole = "agent",
  canAccessAdmin = false,
  canReviewContent = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isElevatedUser = userRole === "admin" || userRole === "manager";
  const isAgentUser = userRole === "agent";
  const usesDemoDashboardLayout = isDemo || isAgentUser;
  const capabilities = React.useMemo(
    () => buildCapabilities({ role: userRole, isDemo, isMobile }),
    [userRole, isDemo, isMobile]
  );
  const showNeedsAttentionMobile = isMobile && capabilities.canSeeNeedsAttention;
  const showActivityChart = capabilities.canSeeTeamActivity;
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = usePersistedState<"7d" | "30d" | "90d">(
    "dashboard.timeRange",
    "7d",
    { validate: isTimeRange }
  );
  const latestTimeRangeRef = useRef(timeRange);
  latestTimeRangeRef.current = timeRange;
  const dashboardRequestEpochRef = useRef(0);
  const dashboardFetchRef = useRef<() => Promise<void>>(async () => undefined);
  const dashboardRefreshRef = useRef<ReturnType<typeof createSingleFlightRefresh> | null>(null);
  if (dashboardRefreshRef.current === null) {
    dashboardRefreshRef.current = createSingleFlightRefresh(
      () => dashboardFetchRef.current(),
      () => document.visibilityState === "visible",
      (error) => console.warn("Dashboard refresh failed:", error)
    );
  }
  const requestDashboardRefresh = useCallback(() => {
    dashboardRefreshRef.current?.request();
  }, []);
  const [refreshing, setRefreshing] = useState(false);
  const [showPerformers, setShowPerformers] = usePersistedState<boolean>(
    "dashboard.showPerformers",
    false,
    { validate: isBooleanPreference }
  );
  const [chartModePreference, setChartModePreference] =
    usePersistedState<DashboardChartMode>("dashboard.chartMode", "leads", { validate: isChartMode });

  // Current user
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  // State
  const [stats, setStats] = useState<DashboardStats>({
    total_clients: 0,
    active_clients: 0,
    new_leads: 0,
    urgent_clients: 0,
    overdue_tasks: 0,
  });
  const [clientsByStatus, setClientsByStatus] = useState<
    Record<string, number>
  >({});
  const [clientsByStatusList, setClientsByStatusList] = useState<
    Record<string, Client[]>
  >({});
  const [recentActivities, setRecentActivities] = useState<Activity[]>([]);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [pendingTasks, setPendingTasks] = useState<Task[]>([]);
  const [agentSavedSearches, setAgentSavedSearches] = useState<
    DashboardSavedSearch[]
  >([]);
  const [clientsNeedingAttention, setClientsNeedingAttention] = useState<
    Client[]
  >([]);
  const pipelineCardRef = React.useRef<HTMLDivElement>(null);
  const [pipelineCardHeight, setPipelineCardHeight] = useState<number | null>(null);
  const [urgentClients, setUrgentClients] = useState<Client[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [agentActivityCounts, setAgentActivityCounts] = useState<
    Record<string, number>
  >({});
  const [dashboardLeads, setDashboardLeads] = useState<DashboardLeadRow[]>([]);
  const [leadOpportunitiesUnavailable, setLeadOpportunitiesUnavailable] = useState(false);
  // Ch76 · the Admin page's `leads.unclaimed` figure, delivered with the feed.
  const [leadUnclaimedTruth, setLeadUnclaimedTruth] = useState<UnclaimedLeadTruth | null>(null);
  const [dashboardRefreshWarning, setDashboardRefreshWarning] = useState<string | null>(null);

  // Chart data
  const [activityChartData, setActivityChartData] = useState<any[]>([]);
  const [teamActivityUnavailable, setTeamActivityUnavailable] = useState(false);

  // Filters
  const [selectedAgents, setSelectedAgents] = useState<string[]>([]);
  const [showSearches, setShowSearches] = usePersistedState<boolean>(
    "dashboard.showSearches",
    false,
    { validate: isBooleanPreference }
  );
  const [recentActivityExpanded, setRecentActivityExpanded] = useState(false);
  const [agentFilter, setAgentFilter] = useState<string>(
    isElevatedUser ? "all" : "mine"
  ); // "mine" | "all" | specific agent id

  // Dialogs
  const [tasksAnchorEl, setTasksAnchorEl] = useState<HTMLElement | null>(null);
  const [urgentAnchorEl, setUrgentAnchorEl] = useState<HTMLElement | null>(
    null
  );
  const [invoiceSentAnchorEl, setInvoiceSentAnchorEl] =
    useState<HTMLElement | null>(null);
  const [stagePopover, setStagePopover] = useState<{
    anchorEl: HTMLElement;
    stage: { key: string; label: string; color: string };
  } | null>(null);
  const [selectedLeadBucket, setSelectedLeadBucket] =
    useState<WeeklyLeadBucket | null>(null);
  const [leadWeekDrilldownBucket, setLeadWeekDrilldownBucket] =
    useState<WeeklyLeadBucket | null>(null);
  const [showAllAttention, setShowAllAttention] = usePersistedState<boolean>(
    "dashboard.showAllAttention",
    false,
    { validate: isBooleanPreference }
  );
  // PKT-0245 · bump to force the "Tasks Due Today" rail to refetch after an
  // inline convert seeds fresh due-today tasks (else they stay hidden until remount).
  const [taskWorklistRefresh, setTaskWorklistRefresh] = useState(0);
  const [showAllInvoices, setShowAllInvoices] = usePersistedState<boolean>(
    "dashboard.showAllInvoices",
    false,
    { validate: isBooleanPreference }
  );
  const [activityCenterOpen, setActivityCenterOpen] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState<Activity | null>(
    null
  );

  const agentNameById = useMemo(
    () =>
      Object.fromEntries(
        agents.map((agent) => [agent.id, agent.full_name || "Agent"])
      ),
    [agents]
  );

  // Map of agent.id → full Agent object · used by the Incoming Leads popover
  // to render the assigned agent's avatar in place of the Claim button when
  // a lead has been claimed. Built once per agents change · O(1) lookup per row.
  const agentsByIdMap = useMemo(
    () => new Map(agents.map((a) => [a.id, a])),
    [agents]
  );

  // Click handler for a lead ROW in the Incoming Leads popover. Routes
  // differently based on whether the lead has already been assigned/converted:
  //
  // - Assigned lead (assigned_agent_id IS NOT NULL · was converted via PR #110
  //   inline-convert OR the existing dialog flow): look up the matching client
  //   by email (case-insensitive · ilike) and navigate with client.id so the
  //   clients page opens the detail sidebar for that client directly. Matches
  //   the navigation pattern already used by handleViewClient + handleTaskClick.
  //
  // - Unassigned lead: fall back to navigating to the clients page leads tab
  //   (existing behavior · operator can verify / convert from there via the
  //   LeadQualityChip Convert-to-client dialog).
  //
  // The "+" button onClick still triggers handleConvertLead (the row click is
  // for navigation · the "+" is for the convert action). Grant directive
  // 2026-05-16: "if the lead has already been assigned and you click on it -
  // it should take you to the clients page and open the sidebar of that
  // client (we have this functionality in other places)".
  const handleLeadRowClick = useCallback(
    async (lead: DashboardLeadRow): Promise<void> => {
      // PKT-0050 · read JOIN-resolved field (was lead.assigned_agent_id
      // before migration 033 dropped the denormalized cache column).
      // If lead is converted/assigned · jump straight to the client detail.
      if (lead.resolved_assigned_agent_id && lead.email) {
        const { data: client } = await supabase
          .from("clients")
          .select("id")
          .ilike("email", lead.email.trim())
          .maybeSingle();
        if (client?.id) {
          onNavigate?.("clients", client.id);
          setSelectedLeadBucket(null);
          return;
        }
        // No matching client by email (edge case · email-changed-at-convert ·
        // OR migration 030 gap "no_matching_client" lead). Fall through to
        // leads tab so the operator can investigate.
      }
      // Unassigned · OR assigned-but-no-matching-client: leads tab fallback.
      onNavigate?.("clients", undefined, { clientsTab: "leads" });
      setSelectedLeadBucket(null);
    },
    [onNavigate]
  );

  // Convert a website lead to a client INLINE from the Incoming Leads popover.
  // Two paths depending on whether the lead has enough data:
  //
  // 1. Complete (has first_name + email): silently create a `clients` row
  //    with assigned_agent_id=currentUserId + flip leads.status='converted'
  //    so the popover row immediately swaps the + button for the operator's
  //    avatar (avatar resolves via the client-email→agent map built by
  //    fetchDashboardData · PKT-0050 · was a mirror column on leads before
  //    migration 033 dropped the cache).
  //    Operator never sees a dialog · 1-click conversion.
  //
  // 2. Incomplete (missing first_name or email): can't safely create a client
  //    row (those fields are required by the existing convert dialog
  //    validation in ClientList.handleConfirmConvert). Fall back to navigating
  //    to the clients page leads tab where the operator can fill in the
  //    missing fields via the existing dialog flow (PR #91 LeadQualityChip
  //    Convert-to-client button).
  //
  // Field mapping mirrors ClientList.handleConvertLead's pre-fill logic
  // verbatim. PKT-0050 + migration 033 dropped the leads.assigned_agent_id
  // mirror · path 1 now writes ONLY clients.assigned_agent_id · the popover
  // resolves agent via the client-email map built by fetchDashboardData
  // (per Grant directive 2026-05-16 "if that lead is already converted ...
  // show that account
  // profile pic with hover text 'assigned to ...'").
  const handleConvertLead = useCallback(
    async (lead: DashboardLeadRow): Promise<void> => {
      if (!currentUserId) return;

      // Parse name into first/last from the single `name` field on the lead.
      const fullName = (lead.name || "").trim();
      const nameParts = fullName.split(/\s+/).filter(Boolean);
      const firstName = nameParts[0] || "";
      const lastName = nameParts.slice(1).join(" ");
      const email = (lead.email || "").trim().toLowerCase();

      // Path 2 · incomplete lead · fall back to navigation.
      if (!firstName || !email) {
        onNavigate?.("clients", undefined, { clientsTab: "leads" });
        setSelectedLeadBucket(null);
        return;
      }

      // Path 1 · inline convert. Mirrors ClientList.handleConfirmConvert.
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        console.error("Convert lead: no authenticated user");
        return;
      }

      // Normalize neighborhoods (DB stores as TEXT comma-sep · frontend may
      // see array or string). Build a string[] for clients.preferred_areas.
      const neighborhoodsRaw = lead.neighborhoods;
      let preferredAreas: string[] | null = null;
      if (Array.isArray(neighborhoodsRaw)) {
        preferredAreas = neighborhoodsRaw
          .map((n) => String(n ?? "").trim())
          .filter(Boolean);
      } else if (typeof neighborhoodsRaw === "string" && neighborhoodsRaw.trim()) {
        preferredAreas = neighborhoodsRaw
          .split(/[,;]/)
          .map((n) => n.trim())
          .filter(Boolean);
      }
      if (lead.neighborhood_other && typeof lead.neighborhood_other === "string") {
        const other = lead.neighborhood_other.trim();
        if (other) {
          preferredAreas = (preferredAreas || []).concat(other);
        }
      }

      // Phone normalization (strip non-digit/non-plus).
      const phone = lead.phone
        ? lead.phone.replace(/[^\d+]/g, "") || null
        : null;

      const clientPayload: Record<string, any> = {
        first_name: firstName,
        last_name: lastName,
        email,
        phone,
        status: "new_client",
        client_type: "renter",
        priority: "normal",
        max_budget: lead.budget_number ?? null,
        preferred_areas: preferredAreas,
        notes: lead.pets ? `Pets: ${lead.pets}` : null,
        // Source is optional attribution. Absence is honest; "website" would
        // invent a fact the lead did not carry.
        ...optionalLeadAttribution(lead.source),
        move_in_timeline: null, // lead doesn't carry a normalized move_in date
        assigned_agent_id: currentUserId,
        user_id: user.id,
        created_by: user.id,
      };

      // Retired direct client INSERT: even this legacy callback delegates to
      // the atomic contract so no dashboard path can bypass claim/conversion.
      void clientPayload; // retained only to make the historical field mapping explicit.
      let convertedClient: { id: string } | null = null;
      let clientErr: unknown = null;
      try {
        const result = await runLeadAction(lead.id, "convert");
        convertedClient = result.client_id ? { id: result.client_id } : null;
      } catch (error) {
        clientErr = error;
      }
      if (clientErr) {
        console.error("Convert lead: failed to create client", clientErr);
        // Common case: duplicate email → fall back to navigate so operator
        // can resolve in the dialog (e.g., merge or adjust email).
        onNavigate?.("clients", undefined, { clientsTab: "leads" });
        setSelectedLeadBucket(null);
        return;
      }

      // Conversion's mandatory next action was committed in the RPC. Cadence
      // expansion remains a separate workflow; do not attach best-effort writes.
      if (convertedClient?.id) {
        setTaskWorklistRefresh((n) => n + 1);
      }

      // PKT-0050 · just flip lead status to 'converted'. The
      // assigned_agent_id used to mirror onto the leads row here (PR #110
      // pattern) but that column was dropped by migration 033 since
      // clients.assigned_agent_id is the single source of truth.
      // Frontend reads now resolve agent via client email match (see
      // resolved_assigned_agent_id population in fetchDashboardData).
      // The same transaction already updated leads.status. Never issue a
      // second best-effort mutation that could create partial truth.
      const leadErr = null;
      if (leadErr) {
        console.warn(
          "Convert lead: client created but lead status update failed",
          leadErr
        );
        // Don't bail · the client exists · just optimistic-update local view.
      }

      // Optimistic local refresh so the popover row swaps + → avatar
      // immediately without a full refetch. PKT-0050 · populate
      // resolved_assigned_agent_id instead of the dropped column ·
      // matches the JOIN-resolved field downstream reads use.
      setDashboardLeads((prev) =>
        prev.map((l) =>
          l.id === lead.id
            ? {
                ...l,
                status: "converted",
                resolved_assigned_agent_id: currentUserId,
              }
            : l
        )
      );
    },
    [currentUserId, onNavigate]
  );

  // "New Leads" claim feed — every unclaimed website lead, newest first.
  // Replaces "Needs Attention" for elevated operators (who alone have the
  // leads data). A claimed lead drops out on the next render (handleConvertLead
  // flips it to converted locally).
  const newLeads = useMemo(() => selectNewLeads(dashboardLeads), [dashboardLeads]);
  // Optimistic local resolve: the row leaves the feed and the all-time figure
  // steps down by one, so the badge and the Admin page stay one number until
  // the next refetch. Ch76: `unclaimed: false` is the backend stamp's own
  // vocabulary, so a claimed row does not have to wait for the refetch.
  const handleLeadResolved = (lead: DashboardLeadRow, ownerUserId: string, action: LeadActionKind) => {
    setDashboardLeads((rows) =>
      rows.map((row) =>
        row.id === lead.id
          ? {
              ...row,
              status: action === "convert" ? "converted" : row.status,
              resolved_assigned_agent_id: ownerUserId,
              unclaimed: false,
            }
          : row
      )
    );
    if (isUnclaimedRow(lead)) {
      setLeadUnclaimedTruth((truth) =>
        truth && truth.status === "ok" && truth.total !== null
          ? { ...truth, total: Math.max(truth.total - 1, 0) }
          : truth
      );
    }
  };

  // Canonical conversion entry point for legacy dashboard surfaces.
  const handleAtomicConvertLead = useCallback(async (lead: DashboardLeadRow) => {
    try {
      const result = await runLeadAction(lead.id, "convert");
      setDashboardLeads((rows) => rows.map((row) => row.id === lead.id ? {
        ...row, status: "converted", resolved_assigned_agent_id: result.owner_user_id,
      } : row));
      setTaskWorklistRefresh((value) => value + 1);
    } catch (error) {
      console.error("Atomic lead conversion failed", error);
      onNavigate?.("clients", undefined, { clientsTab: "leads" });
      setSelectedLeadBucket(null);
    }
  }, [onNavigate]);

  // Read-only lead detail overlay (opened by clicking a New Leads feed row).
  const [leadDetail, setLeadDetail] = useState<DashboardLeadRow | null>(null);

  const [activityDetailOpen, setActivityDetailOpen] = useState(false);

  const chartMode: DashboardChartMode = isElevatedUser
    ? chartModePreference
    : "activity";
  const leadsRangeDays = timeRange === "7d" ? 7 : timeRange === "30d" ? 30 : 90;
  const leadsChartData = useMemo(
    () => buildLeadRangeChart(dashboardLeads, leadsRangeDays),
    [dashboardLeads, leadsRangeDays]
  );
  const leadWeekDrilldownChartData = useMemo(
    () =>
      leadWeekDrilldownBucket
        ? buildLeadDailyChartForRange(
            dashboardLeads,
            leadWeekDrilldownBucket.isoDate,
            leadWeekDrilldownBucket.endIsoDate
          )
        : [],
    [dashboardLeads, leadWeekDrilldownBucket]
  );
  // Mobile leads view (Grant 2026-07-11): 30D/90D bar views are unreadable
  // at phone width (colliding labels, sliver bars), so mobile always charts
  // the last 7 days and shows 30D/90D as TOTAL stats where the range toggle
  // sits on desktop. Desktop behavior is unchanged.
  const useMobileLeadsView = isMobile && chartMode === "leads";
  const mobileLeadsChartData = useMemo(
    () => buildLeadRangeChart(dashboardLeads, 7),
    [dashboardLeads]
  );
  const leads30dTotal = useMemo(
    () =>
      buildLeadRangeChart(dashboardLeads, 30).reduce(
        (sum, point) => sum + point.count,
        0
      ),
    [dashboardLeads]
  );
  const leads90dTotal = useMemo(
    () =>
      buildLeadRangeChart(dashboardLeads, 90).reduce(
        (sum, point) => sum + point.count,
        0
      ),
    [dashboardLeads]
  );
  const visibleLeadsChartData = useMobileLeadsView
    ? mobileLeadsChartData
    : leadWeekDrilldownBucket
    ? leadWeekDrilldownChartData
    : leadsChartData;
  const leadsInRangeCount = useMemo(
    () =>
      (useMobileLeadsView ? mobileLeadsChartData : leadsChartData).reduce(
        (sum, point) => sum + point.count,
        0
      ),
    [useMobileLeadsView, mobileLeadsChartData, leadsChartData]
  );
  const selectedLeadBucketItems = useMemo(
    () =>
      selectedLeadBucket
        ? getWebsiteLeadsForBucketRange(dashboardLeads, selectedLeadBucket)
        : [],
    [dashboardLeads, selectedLeadBucket]
  );

  const handleLeadBucketSelect = (bucket: WeeklyLeadBucket | undefined) => {
    if (!bucket) return;
    if (
      leadsRangeDays === 90 &&
      !leadWeekDrilldownBucket &&
      isMultiDayLeadBucket(bucket)
    ) {
      setLeadWeekDrilldownBucket(bucket);
    }
    setSelectedLeadBucket(bucket);
  };

  const handleLeadDrilldownReset = () => {
    setLeadWeekDrilldownBucket(null);
    setSelectedLeadBucket(null);
  };

  const displayedAgentSavedSearches = useMemo(
    () => agentSavedSearches.slice(0, 5),
    [agentSavedSearches]
  );

  useEffect(() => {
    setSelectedLeadBucket(null);
    setLeadWeekDrilldownBucket(null);
  }, [timeRange]);

  useEffect(() => {
    // Keep the recent-activity filter aligned with role capabilities.
    if (isElevatedUser && agentFilter === "mine") {
      setAgentFilter("all");
    }
    if (!isElevatedUser && agentFilter === "all") {
      setAgentFilter("mine");
    }
  }, [isElevatedUser, agentFilter]);

  // Derived: agent color map
  const agentColorMap = useMemo(() => {
    const map: Record<string, string> = {};
    agents.forEach((agent, i) => {
      map[agent.id] = AGENT_COLORS[i % AGENT_COLORS.length];
    });
    return map;
  }, [agents]);

  const fetchDashboardData = async (requestedTimeRange: DashboardTimeRange) => {
    const requestEpoch = ++dashboardRequestEpochRef.current;
    setRefreshing(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!dashboardRequestIsCurrent(
        requestEpoch,
        dashboardRequestEpochRef.current,
        requestedTimeRange,
        latestTimeRangeRef.current
      )) {
        return;
      }
      if (!user) {
        setLoading(false);
        setRefreshing(false);
        return;
      }
      setCurrentUserId(user.id);

      // Compute activities date range
      const days = dashboardDaysForRange(requestedTimeRange);
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);
      // Compute first-of-month for revenue query
      const firstOfMonth = new Date();
      firstOfMonth.setDate(1);
      firstOfMonth.setHours(0, 0, 0, 0);

      let aggregateActivityFailed = false;
      let leadOpportunitiesFailed = false;
      let leadUnclaimedTruthFromFeed: UnclaimedLeadTruth | null = null;
      const activityOverviewPromise = isDemo || !capabilities.canSeeTeamActivity
        ? Promise.resolve<DashboardActivityOverview | null>(null)
        : fetchWithAuth<DashboardActivityOverview>(
            `/analytics/dashboard-activity?days=${days}`
          ).catch((error) => {
            console.warn("Dashboard aggregate activity endpoint failed:", error);
            aggregateActivityFailed = true;
            return null;
          });

      // Fire all queries in parallel
      const [
        activityOverview,
        profilesResult,
        clientsResult,
        leadsResult,
        weeklyLeadsResult,
        leadClaimsResult,
        tasksResult,
        activitiesResult,
        invoicesResult,
        savedSearchesResult,
      ] = await Promise.all([
        activityOverviewPromise,
        supabase
          .from("profiles")
          .select("id, full_name, email, avatar_url")
          .order("full_name"),
        supabase
          .from("clients")
          .select(
            "id, status, priority, last_contacted_at, first_name, last_name, email, assigned_agent_id"
          ),
        supabase
          .from("leads")
          .select("id, status")
          .or("status.is.null,status.eq.new"),
        capabilities.canSeeLeadOpportunities
          ? fetchLeadOpportunities(90, 500)
              .then((response) => {
                leadUnclaimedTruthFromFeed = response.unclaimed ?? null;
                return { data: response.leads, error: null };
              })
              .catch((error) => {
                console.warn("Lead opportunity endpoint failed:", error);
                leadOpportunitiesFailed = true;
                return { data: null as DashboardLeadRow[] | null, error };
              })
          : Promise.resolve({ data: [] as DashboardLeadRow[], error: null }),
        isElevatedUser
          ? supabase.from("lead_claims").select("lead_id, owner_user_id, converted_client_id")
          : Promise.resolve({ data: [], error: null }),
        supabase
          .from("client_tasks")
          .select("*, clients(id, first_name, last_name)")
          .eq("status", "pending")
          .order("due_date", { ascending: true, nullsFirst: false })
          .order("created_at", { ascending: false }),
        supabase
          .from("activities")
          .select("*")
          .gte("created_at", startDate.toISOString())
          .order("created_at", { ascending: false }),
        supabase
          .from("invoices")
          .select(
            "id, client_id, client_name, amount, status, sent_at, paid_at, created_at, invoice_number"
          )
          .or(`status.in.(sent,overdue),and(status.eq.paid,paid_at.gte.${firstOfMonth.toISOString()})`)
          .order("sent_at", { ascending: false }),
        supabase
          .from("saved_searches")
          .select("id, client_id, name, search_criteria, result_count, created_at")
          .order("created_at", { ascending: false })
          .limit(25),
      ]);

      // A range change can arrive while the previous request is in flight.
      // Never let those older rows repaint the newly selected range; the
      // single-flight coordinator will run one latest-range trailing refresh.
      if (!dashboardRequestIsCurrent(
        requestEpoch,
        dashboardRequestEpochRef.current,
        requestedTimeRange,
        latestTimeRangeRef.current
      )) {
        return;
      }
      const profilesData = profilesResult.data;
      const clients = clientsResult.data;
      const leads = leadsResult.data;
      const weeklyLeads = weeklyLeadsResult.data;
      const leadClaims = leadClaimsResult.data;
      const tasks = tasksResult.data;
      const activities = activitiesResult.data;
      const invoices = invoicesResult.data;
      const savedSearches = savedSearchesResult.data;
      const partialReadFailed = Boolean(
        aggregateActivityFailed ||
        leadOpportunitiesFailed ||
        profilesResult.error ||
        clientsResult.error ||
        leadsResult.error ||
        weeklyLeadsResult.error ||
        leadClaimsResult.error ||
        tasksResult.error ||
        activitiesResult.error ||
        invoicesResult.error ||
        savedSearchesResult.error
      );
      setDashboardRefreshWarning(
        partialReadFailed
          ? "Some dashboard data could not be refreshed. Showing the last successful values where available."
          : null
      );
      const leadSliceUnavailable = Boolean(
        leadOpportunitiesFailed ||
        weeklyLeadsResult.error ||
        clientsResult.error ||
        leadClaimsResult.error
      );
      setLeadOpportunitiesUnavailable(leadSliceUnavailable);

      // PKT-0050 · client-side JOIN: build a Map<email_lowered, agent_id>
      // from the clients fetch · then enrich each weekly lead with
      // `resolved_assigned_agent_id` via the email match. Replaces the
      // denormalized leads.assigned_agent_id cache dropped by migration 033.
      // O(N) build + O(N) enrich · negligible for current data volumes
      // (clients ~50 · leads ~500 cap).
      if (!leadSliceUnavailable) {
        const clientAgentByEmail = new Map<string, string | null>();
        for (const c of (clients || []) as Array<{
          email?: string | null;
          assigned_agent_id?: string | null;
        }>) {
          const key = (c.email || "").trim().toLowerCase();
          if (key) clientAgentByEmail.set(key, c.assigned_agent_id || null);
        }
        const claimOwnerByLeadId = new Map(
          ((leadClaims || []) as Array<{ lead_id: string; owner_user_id: string }>).map((claim) => [
            claim.lead_id, claim.owner_user_id,
          ])
        );
        const enrichedLeads = ((weeklyLeads as DashboardLeadRow[] | null) || []).map(
          (lead) => {
            const key = (lead.email || "").trim().toLowerCase();
            const resolved = claimOwnerByLeadId.get(lead.id) || (key ? clientAgentByEmail.get(key) || null : null);
            return { ...lead, resolved_assigned_agent_id: resolved };
          }
        );
        setDashboardLeads(enrichedLeads);
        setLeadUnclaimedTruth(leadUnclaimedTruthFromFeed);
      }
      if (!invoicesResult.error) setInvoices(invoices || []);

      if (
        !isDemo &&
        isAgentUser &&
        !clientsResult.error &&
        !savedSearchesResult.error
      ) {
        const clientNameById = new Map(
          (clients || []).map((client) => [
            client.id,
            `${client.first_name || ""} ${client.last_name || ""}`.trim() ||
              "Unknown Client",
          ])
        );

        const normalizedSavedSearches: DashboardSavedSearch[] = (
          savedSearches || []
        ).map((search: any) => ({
          id: search.id,
          client_id: search.client_id,
          name: search.name || "Saved Search",
          search_criteria: (search.search_criteria || {}) as DashboardSearchCriteria,
          result_count: search.result_count || 0,
          created_at: search.created_at,
          client_name: clientNameById.get(search.client_id) || "Unknown Client",
        }));
        setAgentSavedSearches(normalizedSavedSearches);
      } else if (isDemo || !isAgentUser) {
        setAgentSavedSearches([]);
      }

      const fetchedAgents: Agent[] | null =
        activityOverview?.agents && activityOverview.agents.length > 0
          ? activityOverview.agents
          : !profilesResult.error
            ? profilesData || []
            : null;
      if (fetchedAgents) setAgents(fetchedAgents);

      if (activityOverview) {
        setTeamActivityUnavailable(false);
        setAgentActivityCounts(activityOverview.activity_counts || {});
        setActivityChartData(activityOverview.chart_data || []);
      } else if (!isDemo && aggregateActivityFailed) {
        // Preserve the last-good team chart; per-user fallback would make a
        // team view silently become a partial personal view.
        setTeamActivityUnavailable(true);
      } else {
        setTeamActivityUnavailable(false);
        setAgentActivityCounts({});
        setActivityChartData([]);
      }

      if (clients) {
        const totalClients = clients.length;
        const activeClients = clients.filter((c) =>
          ["new_client", "touring", "applied", "payment_received"].includes(
            c.status
          )
        ).length;
        const urgentClientList = clients.filter(
          (c) => c.priority === "urgent"
        );
        setUrgentClients(urgentClientList);
        const urgentClients = urgentClientList.length;

        // Count and list clients by status for pipeline
        const statusCounts: Record<string, number> = {};
        const statusLists: Record<string, Client[]> = {};
        clients.forEach((c) => {
          statusCounts[c.status] = (statusCounts[c.status] || 0) + 1;
          if (!statusLists[c.status]) statusLists[c.status] = [];
          statusLists[c.status].push(c);
        });
        setClientsByStatus(statusCounts);
        setClientsByStatusList(statusLists);

        // Clients needing attention (not contacted in 7+ days)
        let needAttention: Client[];

        if (isDemo) {
          // Demo mode: always show only Jane Demo
          needAttention = clients.filter(
            (c) => c.first_name === "Jane" && c.last_name === "Demo"
          );
        } else {
          const sevenDaysAgo = new Date();
          sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
          needAttention = clients
            .filter((c) => {
              if (c.status === "closed" || c.status === "out_of_contact")
                return false;
              if (!c.last_contacted_at) return true;
              return new Date(c.last_contacted_at) < sevenDaysAgo;
            })
            .sort((a, b) => {
              const aTime = a.last_contacted_at
                ? new Date(a.last_contacted_at).getTime()
                : 0;
              const bTime = b.last_contacted_at
                ? new Date(b.last_contacted_at).getTime()
                : 0;
              return aTime - bTime;
            })
            .slice(0, 5);
        }

        setClientsNeedingAttention(needAttention);

        setStats((prev) => ({
          ...prev,
          total_clients: totalClients,
          active_clients: activeClients,
          urgent_clients: urgentClients,
        }));
      }

      if (leads) {
        setStats((prev) => ({ ...prev, new_leads: leads.length }));
      }

      if (tasks) {
        setPendingTasks(tasks);
        const overdueCount = tasks.filter(
          (t) => t.due_date && new Date(t.due_date) < new Date()
        ).length;
        setStats((prev) => ({ ...prev, overdue_tasks: overdueCount }));
      }

      if (!activitiesResult.error) {
        const activityRows = activities || [];
        // Build agentsById map once (O(n) instead of O(n*m) lookup)
        const agentsById = new Map((fetchedAgents ?? agents).map((a) => [a.id, a]));

        // Attach user info to activities
        const activitiesWithUsers = activityRows.map((a) => ({
          ...a,
          user: agentsById.get(a.user_id),
        }));

        // Store raw activities; filtering happens client-side via useMemo
        setRecentActivities(activitiesWithUsers);
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      if (dashboardRequestIsCurrent(
        requestEpoch,
        dashboardRequestEpochRef.current,
        requestedTimeRange,
        latestTimeRangeRef.current
      )) {
        setDashboardRefreshWarning(
          "Dashboard refresh failed. Showing the last successful values where available."
        );
      }
    } finally {
      if (dashboardRequestIsCurrent(
        requestEpoch,
        dashboardRequestEpochRef.current,
        requestedTimeRange,
        latestTimeRangeRef.current
      )) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  };

  dashboardFetchRef.current = () => fetchDashboardData(latestTimeRangeRef.current);

  // The New Leads / lead-opportunities fetch is gated on
  // `capabilities.canSeeLeadOpportunities` (see fetchDashboardData). That
  // capability keys off `userRole`, which App.tsx resolves ASYNCHRONOUSLY
  // from the profiles row: until the role verifies it falls back to "agent"
  // (App.tsx:488 `capabilitiesVerified ? verifiedUserRole : "agent"`), so an
  // admin's capability is FALSE on first mount and only flips true once the
  // role lands. Without depending on it here, the single mount-time fetch ran
  // while the capability was still false, /lead-opportunities was never
  // called, and the widget showed "all caught up" even though the same admin
  // JWT is accepted server-side (require_production_admin) and returns real
  // unclaimed leads. Re-request when the capability flips so the fetch honors
  // the same authorization the endpoint does. (PKT-0616, Ch76.)
  useEffect(() => {
    requestDashboardRefresh();
  }, [timeRange, requestDashboardRefresh, capabilities.canSeeLeadOpportunities]);

  useEffect(
    () => () => {
      dashboardRequestEpochRef.current += 1;
      dashboardRefreshRef.current?.cancelPending();
    },
    []
  );

  // Measure pipeline card height for matching Needs Attention card.
  useEffect(() => {
    if (!usesDemoDashboardLayout || !pipelineCardRef.current) return;
    const measure = () => {
      if (pipelineCardRef.current) {
        setPipelineCardHeight(pipelineCardRef.current.offsetHeight);
      }
    };
    const timer = setTimeout(measure, 100);
    window.addEventListener("resize", measure);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", measure);
    };
  }, [usesDemoDashboardLayout, loading]);

  // Realtime: refetch when activities/clients/tasks change so all logged-in
  // users see live updates from teammates.
  useEffect(() => {
    const channel = supabase
      .channel("dashboard-live")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "activities" },
        () => requestDashboardRefresh()
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "clients" },
        () => requestDashboardRefresh()
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "client_tasks" },
        () => requestDashboardRefresh()
      )
      // Leads must stream in live: the admin header swapped the refresh
      // button for the Admin door (Grant 2026-07-11), so a focused tab has
      // no manual pull — a new website lead has to arrive on its own.
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "leads" },
        () => requestDashboardRefresh()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [requestDashboardRefresh]);

  // One app-level foreground signal coalesces focus + visibility after a real
  // away epoch. Hidden realtime requests remain pending and catch up here.
  useEffect(() => {
    return subscribeAppResume(() => requestDashboardRefresh());
  }, [requestDashboardRefresh]);

  // Local filter for activity feed — instant, no refetch.
  const visibleActivities = useMemo(() => {
    let filtered = recentActivities;
    if (agentFilter === "mine" && currentUserId) {
      filtered = filtered.filter((a) => a.user_id === currentUserId);
    }
    if (!showSearches) {
      filtered = filtered.filter((a) => a.activity_type !== "search_executed");
    }
    return filtered.slice(0, 10);
  }, [recentActivities, agentFilter, showSearches, currentUserId]);

  // Handlers
  const handleViewClient = (clientId: string) => {
    onNavigate?.("clients", clientId);
  };

  const handleActivityClick = (activity: Activity) => {
    setSelectedActivity(activity);
    setActivityDetailOpen(true);
  };

  const handleTaskClick = (task: Task) => {
    if (task.clients?.id) {
      onNavigate?.("clients", task.clients.id, { tab: "activity" });
    }
  };

  // Sorted agents for leaderboard
  const leaderboardAgents = useMemo(() => {
    return [...agents]
      .map((agent) => ({
        ...agent,
        count: agentActivityCounts[agent.id] || 0,
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
  }, [agents, agentActivityCounts]);

  const showInlinePerformers = showPerformers && (isElevatedUser || isMobile);
  const chartCardTitle = showInlinePerformers
    ? "Top Performers"
    : chartMode === "leads"
    ? "Incoming Leads"
    : "Agent Activity";

  // Agents to show in chart (filtered)
  const chartAgents = useMemo(() => {
    if (selectedAgents.length > 0) {
      return agents.filter((a) => selectedAgents.includes(a.id));
    }
    return agents;
  }, [agents, selectedAgents]);

  const activityChartDisplayData = useMemo(
    () =>
      timeRange === "90d"
        ? buildWeeklyActivityChartData(activityChartData)
        : activityChartData,
    [activityChartData, timeRange]
  );

  // Outstanding invoices: unpaid = 'sent' OR 'overdue' (the dunning sweep flips
  // aged invoices sent->overdue; filtering to 'sent' only made the MOST overdue
  // ones vanish from this card). Sorted oldest first (most overdue at top).
  const outstandingInvoices = useMemo(() => {
    return invoices
      .filter((inv) => inv.status === "sent" || inv.status === "overdue")
      .sort((a, b) => {
        const aTime = a.sent_at ? new Date(a.sent_at).getTime() : 0;
        const bTime = b.sent_at ? new Date(b.sent_at).getTime() : 0;
        return aTime - bTime;
      });
  }, [invoices]);

  // Unique client count for Invoice Sent pipeline stage
  const invoiceSentClientCount = useMemo(() => {
    const ids = new Set<string>();
    outstandingInvoices.forEach((inv) => {
      if (inv.client_id) ids.add(inv.client_id);
    });
    return ids.size;
  }, [outstandingInvoices]);

  const outstandingTotal = useMemo(() => {
    return outstandingInvoices.reduce(
      (sum, inv) => sum + (Number(inv.amount) || 0),
      0
    );
  }, [outstandingInvoices]);

  // Revenue this month: paid invoices with paid_at in current month
  const revenueThisMonth = useMemo(() => {
    const firstOfMonth = new Date();
    firstOfMonth.setDate(1);
    firstOfMonth.setHours(0, 0, 0, 0);
    return invoices
      .filter(
        (inv) =>
          inv.status === "paid" &&
          inv.paid_at &&
          new Date(inv.paid_at) >= firstOfMonth
      )
      .reduce((sum, inv) => sum + (Number(inv.amount) || 0), 0);
  }, [invoices]);

  const formatCurrency = (n: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(n);

  // Pipeline totals (invoice_sent comes from invoice data, not clients.status)
  const pipelineTotal = useMemo(() => {
    return PIPELINE_STAGES.reduce((sum, stage) => {
      if (stage.key === "invoice_sent") return sum + invoiceSentClientCount;
      return sum + (clientsByStatus[stage.key] || 0);
    }, 0);
  }, [clientsByStatus, invoiceSentClientCount]);

  return (
    <Box sx={{ pb: 4 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <PageHeaderTitle
          icon={<DashboardIcon />}
          title="Dashboard"
          subtitle="Welcome back! Here's what's happening with your clients."
        />
        {/* Admins get direct doors to the daily Content review workflow and
            the system console; everyone else keeps the refresh action. Both
            destinations remain role-gated again at App and API level. */}
        {canAccessAdmin && onNavigate ? (
          <Stack direction="row" spacing={1}>
            {canReviewContent && <Button
              variant="contained"
              size={isMobile ? "small" : "medium"}
              startIcon={<ContentIcon sx={{ fontSize: isMobile ? 15 : undefined }} />}
              onClick={() => onNavigate("content")}
              sx={{
                minHeight: 44,
                minWidth: isMobile ? 96 : 120,
                px: isMobile ? 1.25 : 2.25,
                fontSize: isMobile ? "0.8rem" : "0.9rem",
                borderRadius: isMobile ? 2 : 2.5,
                whiteSpace: "nowrap",
              }}
            >
              Content
            </Button>}
            <Button
              variant="outlined"
              size={isMobile ? "small" : "medium"}
              aria-label="Open Admin"
              startIcon={
                <AdminPanelSettingsIcon
                  sx={{ fontSize: isMobile ? 15 : undefined }}
                />
              }
              onClick={() => onNavigate("admin")}
              sx={{
                minHeight: 44,
                minWidth: isMobile ? 44 : 120,
                px: isMobile ? 1.25 : 2.25,
                fontSize: isMobile ? "0.8rem" : "0.9rem",
                borderRadius: isMobile ? 2 : 2.5,
                whiteSpace: "nowrap",
                "& .MuiButton-startIcon": { mr: isMobile ? 0 : 1 },
              }}
            >
              <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                Admin
              </Box>
            </Button>
          </Stack>
        ) : (
          <PageRefreshButton loading={refreshing} onClick={requestDashboardRefresh} />
        )}
      </Box>

      {dashboardRefreshWarning && (
        <Alert severity="info" sx={{ mb: 3 }} data-testid="dashboard-refresh-warning">
          {dashboardRefreshWarning}
        </Alert>
      )}

      {/* Loading bar moved to render above Client Pipeline section
          (Grant directive 2026-04-25 — "above client pipeline"). */}

      {/* Stats Cards — hidden for demo and simplified agent view */}
      {!usesDemoDashboardLayout && (isMobile ? (
        <Paper variant="outlined" sx={{ mb: 4, p: 0 }}>
          {(
            [
              {
                label: "Incoming Leads",
                value: stats.new_leads,
                icon: <InboxIcon fontSize="small" />,
                color: "#F59E0B",
                onClick: (_e: React.MouseEvent<HTMLElement>) =>
                  onNavigate?.("clients"),
              },
              {
                label: "Active Clients",
                value: stats.active_clients,
                icon: <CheckCircleIcon fontSize="small" />,
                color: theme.palette.success.main,
                onClick: (_e: React.MouseEvent<HTMLElement>) =>
                  onNavigate?.("clients"),
              },
              {
                label: "Urgent Clients",
                value: stats.urgent_clients,
                icon: <WarningIcon fontSize="small" />,
                color: "#EF4444",
                onClick: (e: React.MouseEvent<HTMLElement>) =>
                  setUrgentAnchorEl(e.currentTarget),
              },
              {
                label: "Pending Tasks",
                value: pendingTasks.length,
                icon: <AssignmentIcon fontSize="small" />,
                color: "#8B5CF6",
                onClick: (e: React.MouseEvent<HTMLElement>) =>
                  setTasksAnchorEl(e.currentTarget),
                sub:
                  stats.overdue_tasks > 0
                    ? `${stats.overdue_tasks} overdue`
                    : undefined,
              },
            ] as Array<{
              label: string;
              value: number;
              icon: React.ReactNode;
              color: string;
              onClick: (e: React.MouseEvent<HTMLElement>) => void;
              sub?: string;
            }>
          ).map((row, idx, arr) => (
            <Box
              key={row.label}
              onClick={(e) => row.onClick(e)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                px: 1.5,
                py: 1.25,
                cursor: "pointer",
                borderBottom:
                  idx < arr.length - 1 ? "1px solid" : "none",
                borderColor: "divider",
                "&:hover": {
                  backgroundColor: alpha(theme.palette.primary.main, 0.04),
                },
              }}
            >
              <Box sx={{ color: row.color, display: "flex" }}>{row.icon}</Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={500} noWrap>
                  {row.label}
                </Typography>
                {row.sub && (
                  <Typography
                    variant="caption"
                    color="error.main"
                    noWrap
                  >
                    {row.sub}
                  </Typography>
                )}
              </Box>
              {loading ? (
                <Skeleton width={32} height={24} />
              ) : (
                <Typography
                  variant="h6"
                  fontWeight={700}
                  sx={{
                    fontVariantNumeric: "tabular-nums",
                    color: row.color,
                    ml: 2,
                  }}
                >
                  {row.value.toLocaleString()}
                </Typography>
              )}
            </Box>
          ))}
        </Paper>
      ) : (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} lg={3}>
            <StatCard
              title="Incoming Leads"
              value={stats.new_leads}
              icon={<InboxIcon />}
              color="#F59E0B"
              loading={loading}
              onClick={() => onNavigate?.("clients")}
              badge={stats.new_leads > 0 ? stats.new_leads : undefined}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <StatCard
              title="Active Clients"
              value={stats.active_clients}
              icon={<CheckCircleIcon />}
              color={theme.palette.success.main}
              gradient={gradients.success}
              loading={loading}
              onClick={() => onNavigate?.("clients")}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <StatCard
              title="Urgent Clients"
              value={stats.urgent_clients}
              icon={<WarningIcon />}
              color="#EF4444"
              loading={loading}
              onClick={(e?: any) =>
                setUrgentAnchorEl(e?.currentTarget || null)
              }
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <StatCard
              title="Pending Tasks"
              value={pendingTasks.length}
              icon={<AssignmentIcon />}
              color="#8B5CF6"
              loading={loading}
              badge={stats.overdue_tasks > 0 ? stats.overdue_tasks : undefined}
              onClick={(e?: any) =>
                setTasksAnchorEl(e?.currentTarget || null)
              }
            />
          </Grid>
        </Grid>
      ))}

      {/* Pipeline Stage Preview Popover */}
      <Popover
        open={Boolean(stagePopover)}
        anchorEl={stagePopover?.anchorEl || null}
        onClose={() => setStagePopover(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        PaperProps={{
          sx: {
            mt: 1,
            width: 360,
            maxHeight: 480,
            backgroundColor: "#1e293b",
            color: "#e2e8f0",
            border: "1px solid #334155",
          },
        }}
      >
        {stagePopover && (
          <Box sx={{ p: 2 }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Box>
                <Typography variant="h6" fontWeight={600}>
                  {stagePopover.stage.label}
                </Typography>
                <Typography variant="caption" sx={{ color: "#94a3b8" }}>
                  {(clientsByStatusList[stagePopover.stage.key] || []).length}{" "}
                  {(clientsByStatusList[stagePopover.stage.key] || []).length ===
                  1
                    ? "client"
                    : "clients"}
                </Typography>
              </Box>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  backgroundColor: stagePopover.stage.color,
                }}
              />
            </Box>
            {(clientsByStatusList[stagePopover.stage.key] || []).length > 0 ? (
              <>
                <Stack spacing={1}>
                  {(clientsByStatusList[stagePopover.stage.key] || [])
                    .slice(0, 6)
                    .map((client) => (
                      <Box
                        key={client.id}
                        onClick={() => {
                          onNavigate?.("clients", client.id);
                          setStagePopover(null);
                        }}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          p: 1,
                          borderRadius: 1,
                          cursor: "pointer",
                          backgroundColor: "#334155",
                          transition: "all 0.2s",
                          "&:hover": {
                            backgroundColor: "#3f4f63",
                            transform: "translateX(4px)",
                          },
                        }}
                      >
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            background: gradients.primary,
                            fontSize: "0.75rem",
                          }}
                        >
                          {client.first_name?.charAt(0)}
                          {client.last_name?.charAt(0)}
                        </Avatar>
                        <Box flex={1} minWidth={0}>
                          <Typography
                            variant="body2"
                            fontWeight={500}
                            sx={{ color: "#e2e8f0" }}
                            noWrap
                          >
                            {client.first_name} {client.last_name}
                          </Typography>
                          {client.priority === "urgent" && (
                            <Typography
                              variant="caption"
                              sx={{ color: "#f87171", fontWeight: 600 }}
                            >
                              Urgent
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    ))}
                </Stack>
                <Button
                  fullWidth
                  size="small"
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => {
                    onNavigate?.("clients", undefined, {
                      status: stagePopover.stage.key as ClientNavigationStatus,
                    });
                    setStagePopover(null);
                  }}
                  sx={{ mt: 1.5, color: "#60a5fa" }}
                >
                  View all{" "}
                  {(clientsByStatusList[stagePopover.stage.key] || []).length}
                </Button>
              </>
            ) : (
              <Box py={3} textAlign="center">
                <Typography variant="body2" sx={{ color: "#94a3b8" }}>
                  No clients in this stage
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Popover>

      {/* Invoice Sent Popover (clients with outstanding invoices) */}
      <Popover
        open={Boolean(invoiceSentAnchorEl)}
        anchorEl={invoiceSentAnchorEl}
        onClose={() => setInvoiceSentAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        PaperProps={{
          sx: {
            mt: 1,
            width: 360,
            maxHeight: 480,
            backgroundColor: "#1e293b",
            color: "#e2e8f0",
            border: "1px solid #334155",
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Box>
              <Typography variant="h6" fontWeight={600}>
                Invoice Sent
              </Typography>
              <Typography variant="caption" sx={{ color: "#94a3b8" }}>
                {invoiceSentClientCount}{" "}
                {invoiceSentClientCount === 1 ? "client" : "clients"} with
                outstanding invoices
              </Typography>
            </Box>
            <ReceiptIcon sx={{ color: "#06B6D4", fontSize: 22 }} />
          </Box>
          {outstandingInvoices.length > 0 ? (
            <>
            <Stack
              divider={<Divider sx={{ borderColor: "#33415550" }} />}
              spacing={0}
            >
              {outstandingInvoices.map((inv) => {
                const daysAgo = inv.sent_at
                  ? Math.floor(
                      (Date.now() - new Date(inv.sent_at).getTime()) / 86400000
                    )
                  : null;
                const isOverdue = daysAgo !== null && daysAgo > 30;
                return (
                  <Box
                    key={inv.id}
                    onClick={() => {
                      if (inv.client_id) {
                        onNavigate?.("clients", inv.client_id);
                        setInvoiceSentAnchorEl(null);
                      }
                    }}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      py: 1,
                      px: 0.5,
                      cursor: "pointer",
                      borderRadius: 1,
                      "&:hover": { backgroundColor: "#33415540" },
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 28,
                        height: 28,
                        fontSize: "0.75rem",
                        background: gradients.primary,
                      }}
                    >
                      {inv.client_name?.charAt(0) || "?"}
                    </Avatar>
                    <Box flex={1} minWidth={0}>
                      <Typography
                        variant="body2"
                        fontWeight={500}
                        noWrap
                        sx={{ color: "#e2e8f0" }}
                      >
                        {inv.client_name || "Unknown"}
                      </Typography>
                      {daysAgo !== null && (
                        <Typography
                          variant="caption"
                          sx={{ color: isOverdue ? "#f87171" : "#64748b" }}
                        >
                          {daysAgo === 0 ? "Sent today" : `${daysAgo}d ago`}
                          {isOverdue && " · overdue"}
                        </Typography>
                      )}
                    </Box>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{ color: "#e2e8f0" }}
                    >
                      {formatCurrency(Number(inv.amount) || 0)}
                    </Typography>
                  </Box>
                );
              })}
            </Stack>
            <Button
              fullWidth
              size="small"
              endIcon={<ArrowForwardIcon />}
              onClick={() => {
                onNavigate?.("clients", undefined, { status: "invoice_sent" });
                setInvoiceSentAnchorEl(null);
              }}
              sx={{ mt: 1.5, color: "#60a5fa" }}
            >
              View all invoice-sent clients
            </Button>
            </>
          ) : (
            <Box py={3} textAlign="center">
              <Typography variant="body2" sx={{ color: "#10b981" }}>
                ✓ No outstanding invoices
              </Typography>
            </Box>
          )}
        </Box>
      </Popover>

      {/* Urgent Clients Popover */}
      <Popover
        open={Boolean(urgentAnchorEl)}
        anchorEl={urgentAnchorEl}
        onClose={() => setUrgentAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            mt: 1,
            width: 360,
            maxHeight: 480,
            backgroundColor: "#1e293b",
            color: "#e2e8f0",
            border: "1px solid #334155",
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Box>
              <Typography variant="h6" fontWeight={600}>
                Urgent Clients
              </Typography>
              <Typography variant="caption" sx={{ color: "#94a3b8" }}>
                {urgentClients.length}{" "}
                {urgentClients.length === 1 ? "client" : "clients"} marked
                urgent
              </Typography>
            </Box>
            <WarningIcon sx={{ color: "#EF4444", fontSize: 22 }} />
          </Box>
          {urgentClients.length > 0 ? (
            <Stack spacing={1}>
              {urgentClients.map((client) => (
                <Box
                  key={client.id}
                  onClick={() => {
                    onNavigate?.("clients", client.id);
                    setUrgentAnchorEl(null);
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    p: 1,
                    borderRadius: 1,
                    cursor: "pointer",
                    backgroundColor: "#334155",
                    transition: "all 0.2s",
                    "&:hover": {
                      backgroundColor: "#3f4f63",
                      transform: "translateX(4px)",
                    },
                  }}
                >
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      background: gradients.primary,
                      fontSize: "0.75rem",
                    }}
                  >
                    {client.first_name?.charAt(0)}
                    {client.last_name?.charAt(0)}
                  </Avatar>
                  <Box flex={1} minWidth={0}>
                    <Typography
                      variant="body2"
                      fontWeight={500}
                      sx={{ color: "#e2e8f0" }}
                      noWrap
                    >
                      {client.first_name} {client.last_name}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: "#94a3b8", textTransform: "capitalize" }}
                    >
                      {client.status?.replace(/_/g, " ")}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Stack>
          ) : (
            <Box py={3} textAlign="center">
              <CheckCircleIcon
                sx={{ fontSize: 40, color: "#10b981", opacity: 0.5, mb: 1 }}
              />
              <Typography variant="body2" sx={{ color: "#94a3b8" }}>
                No urgent clients
              </Typography>
            </Box>
          )}
        </Box>
      </Popover>

      {/* Pending Tasks Popover */}
      <Popover
        open={Boolean(tasksAnchorEl)}
        anchorEl={tasksAnchorEl}
        onClose={() => setTasksAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            mt: 1,
            width: 360,
            maxHeight: 480,
            backgroundColor: "#1e293b",
            color: "#e2e8f0",
            border: "1px solid #334155",
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="h6" fontWeight={600}>
                Pending Tasks
              </Typography>
              {stats.overdue_tasks > 0 && (
                <Chip
                  size="small"
                  label={`${stats.overdue_tasks} overdue`}
                  sx={{
                    backgroundColor: alpha("#ef4444", 0.15),
                    color: "#ef4444",
                    fontWeight: 600,
                  }}
                />
              )}
            </Box>
          </Box>
          {pendingTasks.length > 0 ? (
            <Stack spacing={1.5}>
              {pendingTasks.slice(0, 8).map((task) => (
                <TaskItem
                  key={task.id}
                  task={task}
                  onClick={() => {
                    handleTaskClick(task);
                    setTasksAnchorEl(null);
                  }}
                />
              ))}
              {pendingTasks.length > 8 && (
                <Button
                  size="small"
                  onClick={() => {
                    onNavigate?.("clients");
                    setTasksAnchorEl(null);
                  }}
                  sx={{ mt: 1 }}
                >
                  View {pendingTasks.length - 8} more tasks
                </Button>
              )}
            </Stack>
          ) : (
            <Box py={3} textAlign="center">
              <CheckCircleIcon
                sx={{ fontSize: 40, color: "#10b981", opacity: 0.5, mb: 1 }}
              />
              <Typography variant="body2" sx={{ color: "#94a3b8" }}>
                All caught up!
              </Typography>
            </Box>
          )}
        </Box>
      </Popover>

      {/* Loading bar — placed above Client Pipeline (Grant 2026-04-25 — "above client pipeline") */}
      <LoadingTopBar loading={loading} refreshing={refreshing} />

      {/* Main Content Grid */}
      <Grid container spacing={3}>
        {/* Left Column - Pipeline, Chart, Activity */}
        <Grid item xs={12} lg={8}>
          {/* Pipeline */}
          <Card ref={pipelineCardRef} sx={{ mb: 3 }} data-tour="pipeline-stages">
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={1.5}
              >
                <Typography variant="h6" fontWeight={600}>
                  Client Pipeline
                </Typography>
                <Button
                  size="small"
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => onNavigate?.("clients")}
                  sx={{ textTransform: "none", whiteSpace: "nowrap" }}
                >
                  View All
                </Button>
              </Box>
              <Box display="flex" gap={isMobile ? 0.5 : 2}>
                {PIPELINE_STAGES.map((stage) => (
                  <PipelineStage
                    key={stage.key}
                    stage={stage}
                    compact={isMobile}
                    count={
                      stage.key === "invoice_sent"
                        ? invoiceSentClientCount
                        : clientsByStatus[stage.key] || 0
                    }
                    total={pipelineTotal}
                    onClick={(e) => {
                      if (stage.key === "invoice_sent") {
                        setInvoiceSentAnchorEl(e.currentTarget);
                      } else {
                        setStagePopover({
                          anchorEl: e.currentTarget,
                          stage,
                        });
                      }
                    }}
                  />
                ))}
              </Box>
            </CardContent>
          </Card>

          {/* Mobile, below Pipeline: elevated operators get the New Leads claim
              feed; agent/demo keep Needs Attention below. */}
          {isMobile && capabilities.canSeeLeadOpportunities && (
            <NewLeadsFeed
              leads={newLeads}
              onRowClick={handleLeadRowClick}
              onResolved={(lead, ownerUserId, action) => handleLeadResolved(lead, ownerUserId, action)}
              onViewAll={() =>
                onNavigate?.("clients", undefined, { clientsTab: "leads" })
              }
              cardSx={{ mb: 3 }}
              unavailable={leadOpportunitiesUnavailable}
              loading={loading}
              unclaimedTruth={leadUnclaimedTruth}
              windowDays={90}
            />
          )}
          {isMobile && capabilities.canSeeLeadOpportunities && <TenPctPathLine />}
          {/* Needs Attention — mobile only, agent/demo (elevated see the feed above) */}
          {showNeedsAttentionMobile && !isElevatedUser && (
            <Card sx={{ mb: 3, minHeight: 180, overflow: "auto" }}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                  <AlertIcon sx={{ color: "#ef4444" }} />
                  <Typography variant="h6" fontWeight={600}>Needs Attention</Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
                  Clients not contacted in 7+ days
                </Typography>
                {clientsNeedingAttention.length > 0 ? (
                  <>
                    <Stack spacing={1}>
                      {clientsNeedingAttention
                        .slice(0, showAllAttention ? undefined : 3)
                        .map((client) => (
                          <Box key={client.id} data-tour={client.first_name === "Jane" ? "needs-attention-jane" : undefined}>
                            <ClientAttentionItem
                              client={client}
                              onClick={() => onNavigate?.("clients", client.id)}
                            />
                          </Box>
                        ))}
                    </Stack>
                    {clientsNeedingAttention.length > 3 && (
                      <Button
                        size="small"
                        onClick={() => setShowAllAttention((v) => !v)}
                        sx={{
                          mt: 1,
                          textTransform: "none",
                          fontWeight: 600,
                          width: "100%",
                        }}
                      >
                        {showAllAttention
                          ? "Show less"
                          : `View all (${clientsNeedingAttention.length})`}
                      </Button>
                    )}
                  </>
                ) : (
                  <Box py={2} textAlign="center">
                    <CheckCircleIcon sx={{ fontSize: 32, color: "#10b981", opacity: 0.5, mb: 0.5 }} />
                    <Typography variant="body2" color="text.secondary">All clients contacted recently</Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          {isMobile && isAgentUser && (
            <YourClients
              currentUserId={currentUserId}
              onOpenClient={(clientId) => onNavigate?.("clients", clientId)}
              refreshKey={taskWorklistRefresh}
            />
          )}

          {/* Activity Chart */}
          {showActivityChart && <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems={isMobile ? "stretch" : "center"}
                mb={3}
                // Mobile: two full-width rows with flush edges (title +
                // mode toggle · range + trophy). Desktop: one row as before.
                flexDirection={isMobile ? "column" : "row"}
                flexWrap={isMobile ? "nowrap" : "wrap"}
                gap={isMobile ? 1.25 : 2}
              >
                {/* Left cluster: title + Leads/Activity toggle (Grant 2026-04-25
                    — toggle moved from right stack to next to title; hidden when
                    Top Performers leaderboard is enabled). */}
                <Stack
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  sx={{
                    flex: "none",
                    minWidth: 0,
                    width: { xs: "100%", sm: "auto" },
                    justifyContent: { xs: "space-between", sm: "flex-start" },
                  }}
                >
                  <Typography
                    variant="h6"
                    fontWeight={600}
                    sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
                  >
                    {chartCardTitle}
                  </Typography>
                  {isElevatedUser && !showInlinePerformers && (
                    <ToggleButtonGroup
                      value={chartMode}
                      exclusive
                      size="small"
                      onChange={(_, value) => {
                        if (!value) return;
                        setChartModePreference(value);
                        if (value !== "leads") {
                          setSelectedLeadBucket(null);
                          setLeadWeekDrilldownBucket(null);
                        }
                      }}
                      sx={{
                        flexShrink: 0,
                        // Segmented-pill styling: reads clearly as tappable
                        // (the flat look made stats and buttons swap
                        // affordances — Grant 2026-07-11).
                        "& .MuiToggleButton-root": {
                          px: 1.5,
                          py: 0.6,
                          fontWeight: 700,
                          textTransform: "none",
                          border: `1px solid ${theme.palette.divider}`,
                          color: "text.secondary",
                          "&.Mui-selected": {
                            bgcolor: alpha(theme.palette.primary.main, 0.18),
                            color: "primary.light",
                            borderColor: alpha(theme.palette.primary.main, 0.4),
                            "&:hover": {
                              bgcolor: alpha(theme.palette.primary.main, 0.24),
                            },
                          },
                        },
                        "& .MuiToggleButtonGroup-firstButton": {
                          borderTopLeftRadius: 10,
                          borderBottomLeftRadius: 10,
                        },
                        "& .MuiToggleButtonGroup-lastButton": {
                          borderTopRightRadius: 10,
                          borderBottomRightRadius: 10,
                        },
                      }}
                    >
                      <ToggleButton value="leads">Leads</ToggleButton>
                      <ToggleButton value="activity">Activity</ToggleButton>
                    </ToggleButtonGroup>
                  )}
                </Stack>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{
                    flex: "none",
                    minWidth: 0,
                    width: { xs: "100%", sm: "auto" },
                    justifyContent: { xs: "space-between", sm: "flex-end" },
                  }}
                >
                  {!showInlinePerformers && chartMode === "activity" && (
                    <FormControl
                      size="small"
                      sx={{
                        width: { xs: 128, sm: 200 },
                      }}
                    >
                      <InputLabel>Agents</InputLabel>
                      <Select
                        multiple
                        value={selectedAgents}
                        onChange={(e) =>
                          setSelectedAgents(e.target.value as string[])
                        }
                        input={<OutlinedInput label="Agents" />}
                        renderValue={(selected) => {
                          if (selected.length === 0) return "All Agents";
                          if (selected.length === agents.length)
                            return "All Agents";
                          return `${selected.length} selected`;
                        }}
                      >
                        {agents.map((agent) => (
                          <MenuItem key={agent.id} value={agent.id}>
                            <Checkbox
                              checked={selectedAgents.includes(agent.id)}
                            />
                            <Avatar
                              src={agent.avatar_url}
                              sx={{ width: 24, height: 24, mr: 1 }}
                            >
                              {agent.full_name?.charAt(0)}
                            </Avatar>
                            <ListItemText
                              primary={agent.full_name || agent.email}
                            />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                  {useMobileLeadsView && !showInlinePerformers ? (
                    // Mobile leads view: the chart is always the last 7 days
                    // (highlighted chip); 30D/90D render as total stats in
                    // the range toggle's slot.
                    // Plain-text stats — deliberately NOT chip/button styled
                    // so they don't read as clickable (Grant 2026-07-11).
                    <Stack
                      direction="row"
                      spacing={2.25}
                      alignItems="baseline"
                      sx={{ px: 0.25 }}
                    >
                      {[
                        { label: "7D", value: leadsInRangeCount, active: true },
                        { label: "30D", value: leads30dTotal, active: false },
                        { label: "90D", value: leads90dTotal, active: false },
                      ].map((stat) => (
                        <Box
                          key={stat.label}
                          sx={{
                            display: "flex",
                            alignItems: "baseline",
                            gap: 0.6,
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              color: "text.secondary",
                              fontWeight: 700,
                              letterSpacing: "0.06em",
                              lineHeight: 1,
                            }}
                          >
                            {stat.label}
                          </Typography>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: 800,
                              color: stat.active
                                ? "primary.light"
                                : "text.primary",
                              lineHeight: 1,
                              textTransform: "none",
                            }}
                          >
                            {stat.value}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  ) : (
                    <ToggleButtonGroup
                      value={timeRange}
                      exclusive
                      onChange={(_, value) => value && setTimeRange(value)}
                      size="small"
                      sx={{
                        flexShrink: 0,
                        "& .MuiToggleButton-root": {
                          px: { xs: 1, sm: 1.5 },
                          minWidth: { xs: 44, sm: 58 },
                          fontWeight: 700,
                          textTransform: "none",
                          border: `1px solid ${theme.palette.divider}`,
                          color: "text.secondary",
                          "&.Mui-selected": {
                            bgcolor: alpha(theme.palette.primary.main, 0.18),
                            color: "primary.light",
                            borderColor: alpha(theme.palette.primary.main, 0.4),
                            "&:hover": {
                              bgcolor: alpha(theme.palette.primary.main, 0.24),
                            },
                          },
                        },
                        "& .MuiToggleButtonGroup-firstButton": {
                          borderTopLeftRadius: 10,
                          borderBottomLeftRadius: 10,
                        },
                        "& .MuiToggleButtonGroup-lastButton": {
                          borderTopRightRadius: 10,
                          borderBottomRightRadius: 10,
                        },
                      }}
                    >
                      <ToggleButton value="7d">7D</ToggleButton>
                      <ToggleButton value="30d">30D</ToggleButton>
                      <ToggleButton value="90d">90D</ToggleButton>
                    </ToggleButtonGroup>
                  )}
                  {(isElevatedUser || isMobile) && (
                    <Tooltip
                      title={
                        showInlinePerformers
                          ? chartMode === "leads"
                            ? "Show Lead Chart"
                            : "Show Activity Chart"
                          : "Show Top Performers"
                      }
                    >
                    <IconButton
                      size="small"
                      onClick={() => setShowPerformers(!showPerformers)}
                      sx={{
                        color: showInlinePerformers ? "#F59E0B" : "text.secondary",
                        flexShrink: 0,
                      }}
                    >
                      <TrophyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  )}
                </Stack>
              </Box>
              {showInlinePerformers ? (
                loading ? (
                  <Stack spacing={1}>
                    {Array.from({ length: 3 }).map((_, i) => (
                      <Skeleton key={i} height={40} />
                    ))}
                  </Stack>
                ) : leaderboardAgents.length > 0 ? (
                  <Stack divider={<Divider sx={{ borderColor: "#33415550" }} />}>
                    {leaderboardAgents.map((agent, index) => (
                      <LeaderboardItem
                        key={agent.id}
                        agent={agent}
                        activityCount={agent.count}
                        rank={index + 1}
                        color={
                          index === 0
                            ? "#F59E0B"
                            : index === 1
                            ? "#94a3b8"
                            : index === 2
                            ? "#cd7f32"
                            : "#64748b"
                        }
                      />
                    ))}
                  </Stack>
                ) : (
                  <Box py={3} textAlign="center">
                    <Typography variant="body2" color="text.secondary">
                      {teamActivityUnavailable
                        ? "Team activity is temporarily unavailable"
                        : "No activity data yet"}
                      </Typography>
                    </Box>
                )
              ) : (
                <Box
                  sx={{
                    // Mobile leads view sizes to content (fixed-height chart
                    // + content-height day panel). Desktop/activity keep the
                    // original fixed heights.
                    height: useMobileLeadsView
                      ? "auto"
                      : chartMode === "leads" && selectedLeadBucket
                      ? { xs: 600, md: 300 }
                      : 300,
                  }}
                >
                  {loading ? (
                    <Skeleton variant="rectangular" height={useMobileLeadsView ? 216 : 300} />
                  ) : useMobileLeadsView ? (
                    // ── Mobile leads block (Grant 2026-07-11 redesign) ──
                    // Dedicated simple layout: chunky 7-day chart at a FIXED
                    // pixel height (no %-of-flex chains that can collapse),
                    // then the selected-day panel sized to its content.
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                      {leadsInRangeCount === 0 ? (
                        <Box
                          sx={{
                            height: 150,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            textAlign: "center",
                            px: 2,
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            No website leads in the last 7 days
                          </Typography>
                        </Box>
                      ) : (
                        <Box sx={{ height: 216 }}>
                          <WebsiteLeadLaneChart
                            data={mobileLeadsChartData}
                            selectedBucket={selectedLeadBucket}
                            onSelectBucket={(bucket) =>
                              handleLeadBucketSelect(bucket)
                            }
                            formatBucketTitle={formatWebsiteLeadBucketTitle}
                          />
                        </Box>
                      )}
                      {selectedLeadBucket && (
                        <WebsiteLeadBucketSidebar
                          bucket={selectedLeadBucket}
                          leads={selectedLeadBucketItems}
                          onClose={() => setSelectedLeadBucket(null)}
                          onOpenLeads={() => {
                            onNavigate?.("clients", undefined, {
                              clientsTab: "leads",
                            });
                            setSelectedLeadBucket(null);
                          }}
                          agentsById={agentsByIdMap}
                          currentUserId={currentUserId}
                          onRowClick={handleLeadRowClick}
                          onConvert={handleAtomicConvertLead}
                        />
                      )}
                    </Box>
                  ) : chartMode === "leads" ? (
                    <Box
                      sx={{
                        height: "100%",
                        minHeight: 300,
                        display: "flex",
                        flexDirection: { xs: selectedLeadBucket ? "column" : "row", md: "row" },
                        gap: selectedLeadBucket ? 2 : 0,
                        "& .recharts-wrapper:focus, & .recharts-wrapper:focus-visible, & .recharts-surface:focus, & .recharts-surface:focus-visible":
                          {
                            outline: "none",
                          },
                        "& .recharts-wrapper *:focus, & .recharts-wrapper *:focus-visible":
                          {
                            outline: "none !important",
                          },
                        "& .recharts-active-bar": {
                          display: "none",
                        },
                        "& .recharts-wrapper, & .recharts-surface": {
                          cursor: "pointer",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          height: { xs: selectedLeadBucket ? 300 : "100%", md: "100%" },
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        {!useMobileLeadsView && leadWeekDrilldownBucket && (
                          <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            sx={{
                              mb: 0.75,
                              minHeight: 28,
                              px: 0.25,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#94a3b8",
                                fontWeight: 700,
                                letterSpacing: 0,
                              }}
                              noWrap
                            >
                              Week view: {formatWebsiteLeadBucketTitle(leadWeekDrilldownBucket)}
                            </Typography>
                            <Button
                              size="small"
                              onClick={handleLeadDrilldownReset}
                              sx={{
                                minWidth: 0,
                                px: 0.75,
                                py: 0.25,
                                color: "#93c5fd",
                                textTransform: "none",
                                fontSize: "0.72rem",
                                fontWeight: 800,
                              }}
                            >
                              Back to 90D
                            </Button>
                          </Stack>
                        )}
                        <Box sx={{ flex: 1, minHeight: 0 }}>
                          {leadsInRangeCount === 0 ? (
                            <Box
                              sx={{
                                height: "100%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                textAlign: "center",
                                px: 2,
                              }}
                            >
                              <Typography variant="body2" color="text.secondary">
                                No website leads in this range
                              </Typography>
                            </Box>
                          ) : (
                            <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
                              <WebsiteLeadLaneChart
                                data={visibleLeadsChartData}
                                selectedBucket={selectedLeadBucket}
                                onSelectBucket={(bucket) =>
                                  handleLeadBucketSelect(bucket)
                                }
                                formatBucketTitle={formatWebsiteLeadBucketTitle}
                              />
                            </Box>
                          )}
                        </Box>
                        {!useMobileLeadsView && timeRange === "90d" && !leadWeekDrilldownBucket && (
                          <Typography
                            variant="caption"
                            sx={{
                              mt: 0.75,
                              minHeight: 20,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "#94a3b8",
                              fontWeight: 700,
                              letterSpacing: 0,
                              textAlign: "center",
                            }}
                          >
                            90D is grouped by week.
                          </Typography>
                        )}
                      </Box>
                      {selectedLeadBucket && (
                        <WebsiteLeadBucketSidebar
                          bucket={selectedLeadBucket}
                          leads={selectedLeadBucketItems}
                          onClose={() => setSelectedLeadBucket(null)}
                          onOpenLeads={() => {
                            onNavigate?.("clients", undefined, { clientsTab: "leads" });
                            setSelectedLeadBucket(null);
                          }}
                          agentsById={agentsByIdMap}
                          currentUserId={currentUserId}
                          onRowClick={handleLeadRowClick}
                          onConvert={handleAtomicConvertLead}
                        />
                      )}
                    </Box>
                  ) : teamActivityUnavailable ? (
                    <Box
                      sx={{
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                        px: 2,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Team activity is temporarily unavailable. Try refreshing.
                      </Typography>
                    </Box>
                  ) : chartAgents.length === 0 || activityChartDisplayData.length === 0 ? (
                    <Box
                      sx={{
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                        px: 2,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        No activity data yet
                      </Typography>
                    </Box>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={activityChartDisplayData}>
                        <CartesianGrid
                          strokeDasharray="3 3"
                          stroke={alpha("#fff", 0.05)}
                        />
                        <XAxis
                          dataKey="date"
                          stroke={theme.palette.text.primary}
                          tick={{ fill: theme.palette.text.primary, fontSize: 12 }}
                          interval={timeRange === "90d" ? 0 : "preserveStartEnd"}
                          tickLine={false}
                          axisLine={false}
                        />
                        <YAxis
                          stroke={theme.palette.text.primary}
                          tick={{ fill: theme.palette.text.primary, fontSize: 12 }}
                          tickLine={false}
                          axisLine={false}
                        />
                        <RechartsTooltip
                          content={<ChartTooltip agentNameById={agentNameById} />}
                        />
                        <Legend
                          formatter={(value) => {
                            const agentId = value.replace("_total", "");
                            const agent = agents.find((a) => a.id === agentId);
                            return agent?.full_name?.split(" ")[0] || "Agent";
                          }}
                        />
                        {chartAgents.map((agent) => (
                          <Line
                            key={agent.id}
                            type="monotone"
                            dataKey={`${agent.id}_total`}
                            name={`${agent.id}_total`}
                            stroke={agentColorMap[agent.id]}
                            strokeWidth={timeRange === "90d" ? 2.5 : 2}
                            dot={(props: any) =>
                              renderActivityChartDot(
                                props,
                                timeRange === "90d" ? 5 : 4
                              )
                            }
                            activeDot={(props: any) =>
                              renderActivityChartDot(
                                props,
                                timeRange === "90d" ? 7 : 6
                              )
                            }
                          />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>}

          {/* PKT-0056 · Operator Today dashboard widget · v1.
              Compounds on PR #117 PKT-0049 Phase 1 (MERGED · migration 031
              trigger + 11 new ActivityType literals). Renders compact
              per-category counts for today's activities so operator scans
              morning triage in one glance. Becomes more valuable the moment
              bot events start flowing via PKT-0054 Phase 2 + SMS inbound
              via PKT-0040 + IG inbound via PKT-0036. */}
          <ActivityTodaySummary
            activities={recentActivities}
            isLoading={loading}
          />

          {/* Recent Activity */}
          <Card>
            <CardContent>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
                flexWrap={isMobile ? "nowrap" : "wrap"}
                gap={isMobile ? 1 : 2}
              >
                <Typography
                  variant="h6"
                  fontWeight={600}
                  sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
                >
                  Recent Activity
                </Typography>
                <Stack direction="row" spacing={isMobile ? 1 : 2} alignItems="center">
                  {!isMobile && (
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={showSearches}
                          onChange={(e) => setShowSearches(e.target.checked)}
                        />
                      }
                      label={
                        <Typography variant="caption">Show Searches</Typography>
                      }
                    />
                  )}
                  <FormControl
                    size="small"
                    sx={{ width: { xs: 140, sm: 140 } }}
                  >
                    <Select
                      value={agentFilter}
                      onChange={(e) => setAgentFilter(e.target.value)}
                      displayEmpty
                    >
                      <MenuItem value="mine">My Activity</MenuItem>
                      {isElevatedUser && (
                        <MenuItem value="all">All Agents</MenuItem>
                      )}
                    </Select>
                  </FormControl>
                  {!isMobile && (
                    <Button
                      size="small"
                      endIcon={<ArrowForwardIcon />}
                      onClick={() => setActivityCenterOpen(true)}
                    >
                      View All
                    </Button>
                  )}
                </Stack>
              </Box>
              {loading ? (
                <Stack spacing={1}>
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Box key={i} display="flex" gap={2} py={1.5}>
                      <Skeleton variant="circular" width={36} height={36} />
                      <Box flex={1}>
                        <Skeleton width="80%" height={20} />
                        <Skeleton width="40%" height={16} />
                      </Box>
                    </Box>
                  ))}
                </Stack>
              ) : visibleActivities.length > 0 ? (
                <>
                  <Stack divider={<Divider sx={{ borderColor: "#33415550" }} />}>
                    {(isMobile && !recentActivityExpanded
                      ? visibleActivities.slice(0, 3)
                      : visibleActivities
                    ).map((activity) => (
                      <ActivityItem
                        key={activity.id}
                        activity={activity}
                        onClick={() => handleActivityClick(activity)}
                      />
                    ))}
                  </Stack>
                  {isMobile && visibleActivities.length > 3 && (
                    <Box sx={{ mt: 1.5, display: "flex", justifyContent: "center" }}>
                      <Button
                        fullWidth
                        size="small"
                        onClick={() => setRecentActivityExpanded((v) => !v)}
                        sx={{ textTransform: "none" }}
                      >
                        {recentActivityExpanded
                          ? "Close"
                          : `View All (${visibleActivities.length})`}
                      </Button>
                    </Box>
                  )}
                </>
              ) : (
                <Box py={4} textAlign="center">
                  <HistoryIcon
                    sx={{
                      fontSize: 48,
                      color: "text.secondary",
                      opacity: 0.3,
                      mb: 1,
                    }}
                  />
                  <Typography color="text.secondary">
                    No recent activity
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column - Tasks, Leaderboard, Attention */}
        <Grid item xs={12} lg={4}>
          {/* Outbound Status tile removed: it alarmed ("SMS Live (misconfigured) ·
              no number set") about outbound client-SMS that is intentionally not
              configured, which reads as broken. This rail slot is reserved for
              the "What the Machine Did" feed (Batch-2). */}
          {capabilities.canSeeLeadOpportunities && !isMobile && (
            <NewLeadsFeed
              leads={newLeads}
              onRowClick={handleLeadRowClick}
              onResolved={(lead, ownerUserId, action) => handleLeadResolved(lead, ownerUserId, action)}
              onViewAll={() => onNavigate?.("clients", undefined, { clientsTab: "leads" })}
              cardSx={{ mb: 3 }}
              unavailable={leadOpportunitiesUnavailable}
              loading={loading}
              unclaimedTruth={leadUnclaimedTruth}
              windowDays={90}
            />
          )}
          {capabilities.canSeeLeadOpportunities && !isMobile && <TenPctPathLine />}
          {/* PKT-0916 (C) · read-only money panel, derived from invoices + clients */}
          {!isDemo && <MoneyPanel />}
          {userRole === "admin" && <OperatorConsoleRail />}
          {/* PKT-0306 · "Your Clients" ranked next-action widget (replaces the
              "Tasks due today" worklist). Clients ranked by who needs the operator
              now, one action each; overdue cadence collapses into the score (no
              per-task rows). Click a row → open that client. Read-only; no send.
              The per-client cadence checklist + task CRUD live on client detail. */}
          {!isMobile && (
            <YourClients
              currentUserId={currentUserId}
              onOpenClient={(clientId) => onNavigate?.("clients", clientId)}
              refreshKey={taskWorklistRefresh}
            />
          )}
          {/* Simplified: Needs Attention + Pending Tasks */}
          {usesDemoDashboardLayout && (
            <>
            {/* Needs Attention — matched height with pipeline (desktop only; mobile version rendered inline after Pipeline) */}
            {!isMobile && <Card sx={{ mb: 3, ...(pipelineCardHeight ? { height: pipelineCardHeight } : { minHeight: 180 }), overflow: "auto" }}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                  <AlertIcon sx={{ color: "#ef4444" }} />
                  <Typography variant="h6" fontWeight={600}>Needs Attention</Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
                  Clients not contacted in 7+ days
                </Typography>
                {clientsNeedingAttention.length > 0 ? (
                  <>
                    <Stack spacing={1}>
                      {clientsNeedingAttention
                        .slice(0, showAllAttention ? undefined : 3)
                        .map((client) => (
                          <Box key={client.id} data-tour={client.first_name === "Jane" ? "needs-attention-jane" : undefined}>
                            <ClientAttentionItem
                              client={client}
                              onClick={() => onNavigate?.("clients", client.id)}
                            />
                          </Box>
                        ))}
                    </Stack>
                    {clientsNeedingAttention.length > 3 && (
                      <Button
                        size="small"
                        onClick={() => setShowAllAttention((v) => !v)}
                        sx={{
                          mt: 1,
                          textTransform: "none",
                          fontWeight: 600,
                          width: "100%",
                        }}
                      >
                        {showAllAttention
                          ? "Show less"
                          : `View all (${clientsNeedingAttention.length})`}
                      </Button>
                    )}
                  </>
                ) : (
                  <Box py={2} textAlign="center">
                    <CheckCircleIcon sx={{ fontSize: 32, color: "#10b981", opacity: 0.5, mb: 0.5 }} />
                    <Typography variant="body2" color="text.secondary">All clients contacted recently</Typography>
                  </Box>
                )}
              </CardContent>
            </Card>}

            {/* Pending Tasks with sample data (hidden on mobile) */}
            {!isMobile && <Card sx={{ mb: 3, minHeight: 180 }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                  <Typography variant="h6" fontWeight={600}>Pending Tasks</Typography>
                  <Chip
                    label={isDemo ? Math.max(pendingTasks.length, 3) : pendingTasks.length}
                    size="small"
                    sx={{ fontWeight: 600, bgcolor: alpha("#8B5CF6", 0.15), color: "#8B5CF6" }}
                  />
                </Box>
                {isDemo ? (
                  <Stack spacing={0.5}>
                    {[
                      { title: "Follow up with Jane Demo", subtitle: "Jane Demo · Due tomorrow", icon: <PhoneIcon sx={{ fontSize: 18, color: "#0EA5E9" }} /> },
                      { title: "Schedule tour — The Juniper", subtitle: "Jane Demo · Midtown", icon: <TourIcon sx={{ fontSize: 18, color: "#F59E0B" }} /> },
                      { title: "Send updated listings", subtitle: "Jane Demo · 2 bed options", icon: <EmailIcon sx={{ fontSize: 18, color: "#8B5CF6" }} /> },
                    ].map((task, i) => (
                      <Box
                        key={i}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          p: 1,
                          borderRadius: 1.5,
                          cursor: "pointer",
                          "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.04) },
                        }}
                        onClick={() => {
                          // Find Jane Demo's ID from any status list
                          const allClients = Object.values(clientsByStatusList).flat();
                          const jane = allClients.find((c) => c.first_name === "Jane");
                          onNavigate?.("clients", jane?.id);
                        }}
                      >
                        {task.icon}
                        <Box flex={1} minWidth={0}>
                          <Typography variant="body2" fontWeight={600} noWrap>{task.title}</Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>{task.subtitle}</Typography>
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                ) : loading ? (
                  <Stack spacing={1}>
                    {Array.from({ length: 3 }).map((_, i) => (
                      <Skeleton
                        key={i}
                        variant="rectangular"
                        height={56}
                        sx={{ borderRadius: 2 }}
                      />
                    ))}
                  </Stack>
                ) : pendingTasks.length > 0 ? (
                  <Stack spacing={1}>
                    {pendingTasks.slice(0, 5).map((task) => (
                      <TaskItem
                        key={task.id}
                        task={task}
                        onClick={() => handleTaskClick(task)}
                      />
                    ))}
                  </Stack>
                ) : (
                  <Box py={2} textAlign="center">
                    <CheckCircleIcon sx={{ fontSize: 32, color: "#10b981", opacity: 0.5, mb: 0.5 }} />
                    <Typography variant="body2" color="text.secondary">All caught up!</Typography>
                  </Box>
                )}
              </CardContent>
            </Card>}

            {/* Saved Searches */}
            {(isDemo || isAgentUser) && <Card sx={{ mb: 3 }} data-tour={isDemo ? "saved-searches" : undefined}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <BookmarkIcon sx={{ color: "#0EA5E9" }} />
                    <Typography variant="h6" fontWeight={600}>Saved Searches</Typography>
                  </Box>
                  <Chip
                    label={isDemo ? "2" : displayedAgentSavedSearches.length}
                    size="small"
                    sx={{ fontWeight: 600, bgcolor: alpha("#0EA5E9", 0.15), color: "#0EA5E9" }}
                  />
                </Box>
                {isDemo ? (
                  <Stack spacing={0.5}>
                    {DEMO_SAVED_SEARCHES.map((search, i) => {
                      // Find Jane Demo's ID to navigate with client context
                      const allClients = Object.values(clientsByStatusList).flat();
                      const jane = allClients.find((c) => c.first_name === "Jane");
                      return (
                        <Box
                          key={i}
                          data-tour={i === 0 ? "saved-search-first" : undefined}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            p: 1,
                            borderRadius: 1.5,
                            cursor: "pointer",
                            "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.04) },
                          }}
                          onClick={() =>
                            onNavigate?.("search", jane?.id, {
                              searchCriteria: search.searchCriteria,
                            })
                          }
                        >
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: 1,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              bgcolor: alpha(search.color, 0.1),
                            }}
                          >
                            <SearchIcon sx={{ fontSize: 18, color: search.color }} />
                          </Box>
                          <Box flex={1} minWidth={0}>
                            <Typography variant="body2" fontWeight={600} noWrap>{search.name}</Typography>
                            <Typography variant="caption" color="text.secondary" noWrap>{search.criteria}</Typography>
                          </Box>
                          <Tooltip title="Run search">
                            <IconButton size="small" sx={{ color: "text.secondary" }}>
                              <PlayIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      );
                    })}
                  </Stack>
                ) : loading ? (
                  <Stack spacing={0.5}>
                    {Array.from({ length: 3 }).map((_, i) => (
                      <Skeleton
                        key={i}
                        variant="rectangular"
                        height={48}
                        sx={{ borderRadius: 1.5 }}
                      />
                    ))}
                  </Stack>
                ) : displayedAgentSavedSearches.length === 0 ? (
                  <Box py={2} textAlign="center">
                    <BookmarkIcon sx={{ fontSize: 28, color: "text.disabled", mb: 0.5 }} />
                    <Typography variant="body2" color="text.secondary">
                      No saved searches yet
                    </Typography>
                  </Box>
                ) : (
                  <Stack spacing={0.5}>
                    {displayedAgentSavedSearches.map((search, i) => (
                      <Box
                        key={search.id}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          p: 1,
                          borderRadius: 1.5,
                          cursor: "pointer",
                          "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.04) },
                        }}
                        onClick={() =>
                          onNavigate?.("search", search.client_id, {
                            searchCriteria: search.search_criteria,
                          })
                        }
                      >
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: alpha(AGENT_COLORS[i % AGENT_COLORS.length], 0.12),
                          }}
                        >
                          <SearchIcon
                            sx={{
                              fontSize: 18,
                              color: AGENT_COLORS[i % AGENT_COLORS.length],
                            }}
                          />
                        </Box>
                        <Box flex={1} minWidth={0}>
                          <Typography variant="body2" fontWeight={600} noWrap>
                            {search.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {search.client_name} · {buildSavedSearchSummary(search.search_criteria)}
                          </Typography>
                        </Box>
                        <Tooltip title="Run search">
                          <IconButton size="small" sx={{ color: "text.secondary" }}>
                            <PlayIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ))}
                  </Stack>
                )}
              </CardContent>
            </Card>}
            </>
          )}

          {/* Outstanding Invoices — hidden for simplified agent/demo view */}
          {!usesDemoDashboardLayout && <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={1.5}
              >
                <Box>
                  <Typography variant="h6" fontWeight={600}>
                    Outstanding Invoices
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {outstandingInvoices.length} unpaid ·{" "}
                    {formatCurrency(outstandingTotal)}
                  </Typography>

                </Box>
                <ReceiptIcon sx={{ color: "#f59e0b", fontSize: 22 }} />
              </Box>

              <Box sx={{ mb: 2 }}>
                {loading ? (
                  <Stack spacing={1}>
                    {[1, 2, 3].map((i) => (
                      <Skeleton key={i} height={40} />
                    ))}
                  </Stack>
                ) : outstandingInvoices.length > 0 ? (
                  <Stack
                    divider={<Divider sx={{ borderColor: "#33415550" }} />}
                    spacing={0}
                  >
                    {(showAllInvoices
                      ? outstandingInvoices
                      : outstandingInvoices.slice(0, 3)
                    ).map((inv) => {
                      const daysAgo = inv.sent_at
                        ? Math.floor(
                            (Date.now() - new Date(inv.sent_at).getTime()) /
                              86400000
                          )
                        : null;
                      const isOverdue = daysAgo !== null && daysAgo > 30;
                      return (
                        <Box
                          key={inv.id}
                          onClick={() =>
                            inv.client_id &&
                            onNavigate?.("clients", inv.client_id)
                          }
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            py: 1,
                            px: 0.5,
                            cursor: "pointer",
                            borderRadius: 1,
                            "&:hover": { backgroundColor: "#33415540" },
                          }}
                        >
                          <Avatar
                            sx={{
                              width: 28,
                              height: 28,
                              fontSize: "0.75rem",
                              background: gradients.primary,
                            }}
                          >
                            {inv.client_name?.charAt(0) || "?"}
                          </Avatar>
                          <Box flex={1} minWidth={0}>
                            <Typography
                              variant="body2"
                              fontWeight={500}
                              noWrap
                              sx={{ color: "#e2e8f0" }}
                            >
                              {inv.client_name || "Unknown"}
                            </Typography>
                            {daysAgo !== null && (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: isOverdue ? "#f87171" : "#64748b",
                                }}
                              >
                                {daysAgo === 0
                                  ? "Sent today"
                                  : `${daysAgo}d ago`}
                                {isOverdue && " · overdue"}
                              </Typography>
                            )}
                          </Box>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            sx={{ color: "#e2e8f0" }}
                          >
                            {formatCurrency(Number(inv.amount) || 0)}
                          </Typography>
                        </Box>
                      );
                    })}
                    {outstandingInvoices.length > 3 && (
                      <Box
                        onClick={() => setShowAllInvoices((v) => !v)}
                        sx={{
                          py: 1,
                          textAlign: "center",
                          cursor: "pointer",
                          color: "#60a5fa",
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          "&:hover": { color: "#93c5fd" },
                        }}
                      >
                        {showAllInvoices
                          ? "Show less"
                          : `Show all (${outstandingInvoices.length})`}
                      </Box>
                    )}
                  </Stack>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minHeight: 100,
                    }}
                  >
                    <Typography variant="body2" sx={{ color: "#10b981" }}>
                      ✓ No outstanding invoices
                    </Typography>
                  </Box>
                )}
              </Box>

              <Divider sx={{ borderColor: "#33415570", mb: 1.5 }} />
              <Box>
                <Typography
                  variant="caption"
                  sx={{ color: "#64748b", display: "block" }}
                >
                  Revenue this month
                </Typography>
                <Typography
                  variant="h5"
                  fontWeight={700}
                  sx={{ color: "#10b981" }}
                >
                  {loading ? (
                    <Skeleton width={100} />
                  ) : (
                    formatCurrency(revenueThisMonth)
                  )}
                </Typography>
              </Box>
            </CardContent>
          </Card>}

          {/* New Leads claim feed — desktop-only admin/manager card. Replaces
              the old "Needs Attention" (stale-client) list: elevated operators
              claim unclaimed website leads inline here. Mobile elevated sees
              the same feed inline below Pipeline; agent/demo keep the
              simplified layout's Needs Attention card above. */}
        </Grid>
      </Grid>

      {/* Activity Detail Dialog */}
      <ActivityDetailDialog
        activity={selectedActivity}
        open={activityDetailOpen}
        onClose={() => setActivityDetailOpen(false)}
        onViewClient={handleViewClient}
      />

      {/* Activity Center Dialog */}
      <Dialog
        open={activityCenterOpen}
        onClose={() => setActivityCenterOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: { height: "90vh" } }}
      >
        <ActivityCenter onClose={() => setActivityCenterOpen(false)} />
      </Dialog>

      {/* Read-only New Lead detail — opens over the dashboard (no navigation) */}
      <LeadDetailModal
        lead={leadDetail}
        open={!!leadDetail}
        onClose={() => setLeadDetail(null)}
        canConvert={!!currentUserId}
        onConvert={async (lead) => {
          await handleAtomicConvertLead(lead);
          setLeadDetail(null);
        }}
      />

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </Box>
  );
};

export default ApartmentDashboard;
